import React, { useState, useEffect, useMemo, useRef } from 'react';
import ThemeMaker from '../../components/thememaker';
import { Tabs, Form, Input, Select, Tag, Switch, TimePicker, Button, Table, Modal, Spin, Popconfirm } from 'antd';
import axios from 'axios';
import { useParams, useHistory, useLocation } from "react-router-dom";
import { config } from '../../utils/config';
import { toast } from 'react-toastify';
import "../../index.css";
import dayjs from "dayjs";
import jwt_decode from "jwt-decode";
import { FaWarehouse, FaClock, FaCheckCircle, FaTimesCircle, FaMoneyCheckAlt, FaSyncAlt, FaTruck, FaMapMarkedAlt, FaPlus, FaEdit, FaInfoCircle, FaTrash, FaToggleOn, FaToggleOff } from "react-icons/fa";
import { HiPencilSquare } from "react-icons/hi2";

const formItemLayout = {
    labelCol: {
        xs: { span: 24 },
        sm: { span: 5 },
    },
    wrapperCol: {
        xs: { span: 24 },
        sm: { span: 16 },
    },
};

const tailFormItemLayout = {
    wrapperCol: {
        xs: { span: 24, offset: 0 },
        sm: { span: 16, offset: 5 },
    },
};

export default function Addwarehouse({ initialTab = "1" } = {}) {
    const params = useParams();
    const location = useLocation();
    const router = useHistory();
    const { apiEndpoint } = config;
    const rawToken = sessionStorage.getItem("token") || "";

    const queryParams = new URLSearchParams(location.search);
    const routeWarehouseId = params?.id || queryParams.get("id") || queryParams.get("warehouse_id") || null;
    const routeLocationId = params?.locationId || queryParams.get("locationId") || queryParams.get("warehouse_location_id") || routeWarehouseId;

    const pathname = (location.pathname || "").toLowerCase();
    const getInitialActiveTab = () => {
        const currentQueryParams = new URLSearchParams(window.location.search);
        const fromQuery = currentQueryParams.get("tab");
        if (fromQuery) return fromQuery;

        const currentPath = (window.location.pathname || "").toLowerCase();
        if (currentPath.includes("serviceability")) return "4";
        if (currentPath.includes("fulfillment")) return "3";

        const whKey = routeWarehouseId || "default";
        const savedTab = sessionStorage.getItem(`active_wh_tab_${whKey}`) || sessionStorage.getItem("active_warehouse_tab");
        if (savedTab && ["1", "2", "3", "4", "5"].includes(savedTab)) {
            return savedTab;
        }

        return initialTab || "1";
    };

    const [form] = Form.useForm();
    const [activeTab, setActiveTab] = useState(getInitialActiveTab);
    const prevTabRef = useRef(activeTab);
    const [isLoading, setIsLoading] = useState(false);
    const [isTimingLoading, setIsTimingLoading] = useState(false);
    const [isPincodeLoading, setIsPincodeLoading] = useState(false);
    const [pincodeStatus, setPincodeStatus] = useState(null); // null | 'loading' | 'success' | 'error'

    // Current Active Warehouse ID (from route param or newly created)
    const [effectiveWarehouseId, setEffectiveWarehouseId] = useState(routeWarehouseId ? Number(routeWarehouseId) : null);
    const [effectiveLocationId, setEffectiveLocationId] = useState(routeLocationId || null);

    // Bank Details (View / Edit)
    const [bankDetails, setBankDetails] = useState(null);
    const [isBankLoading, setIsBankLoading] = useState(false);
    const [isBankEditModalOpen, setIsBankEditModalOpen] = useState(false);
    const [isBankUpdating, setIsBankUpdating] = useState(false);
    const [isVerifyingBank, setIsVerifyingBank] = useState(false);
    const [isBankVerifiedInModal, setIsBankVerifiedInModal] = useState(false);
    const [verifiedBankData, setVerifiedBankData] = useState(null);
    const [modalIfscInfo, setModalIfscInfo] = useState({ bank: "", branch: "" });
    const [modalIfscLoading, setModalIfscLoading] = useState(false);
    const [bankEditForm] = Form.useForm();

    // Determine parent_id from token: 1 = seller (editable & updatable), 0/other = showable/read-only
    let parentId = null;
    if (rawToken) {
        try {
            const decoded = jwt_decode(rawToken);
            const userObj = Array.isArray(decoded?.data?.user) ? decoded?.data?.user[0] : (decoded?.data?.user || {});
            parentId = userObj?.parent_id !== undefined ? Number(userObj?.parent_id) : (decoded?.data?.parent_id !== undefined ? Number(decoded?.data?.parent_id) : null);
        } catch (e) {
            console.error("Token decode error in Addwarehouse:", e);
        }
    }
    const isSellerEditable = parentId === 1;

    // Operating hours enable/disable state
    const [isOperatingHoursEnabled, setIsOperatingHoursEnabled] = useState(true);
    const [isTogglingStatus, setIsTogglingStatus] = useState(false);
    const [togglingFulfillmentId, setTogglingFulfillmentId] = useState(null);

    // Warehouse list for selector
    const [warehouseList, setWarehouseList] = useState([]);
    const [selectedTimingWarehouseId, setSelectedTimingWarehouseId] = useState(routeWarehouseId ? Number(routeWarehouseId) : null);

    // State / City data
    const INDIAN_STATES = [
        { label: "Andhra Pradesh", value: "Andhra Pradesh", iso2: "AP" },
        { label: "Arunachal Pradesh", value: "Arunachal Pradesh", iso2: "AR" },
        { label: "Assam", value: "Assam", iso2: "AS" },
        { label: "Bihar", value: "Bihar", iso2: "BR" },
        { label: "Chhattisgarh", value: "Chhattisgarh", iso2: "CT" },
        { label: "Goa", value: "Goa", iso2: "GA" },
        { label: "Gujarat", value: "Gujarat", iso2: "GJ" },
        { label: "Haryana", value: "Haryana", iso2: "HR" },
        { label: "Himachal Pradesh", value: "Himachal Pradesh", iso2: "HP" },
        { label: "Jharkhand", value: "Jharkhand", iso2: "JH" },
        { label: "Karnataka", value: "Karnataka", iso2: "KA" },
        { label: "Kerala", value: "Kerala", iso2: "KL" },
        { label: "Madhya Pradesh", value: "Madhya Pradesh", iso2: "MP" },
        { label: "Maharashtra", value: "Maharashtra", iso2: "MH" },
        { label: "Manipur", value: "Manipur", iso2: "MN" },
        { label: "Meghalaya", value: "Meghalaya", iso2: "ML" },
        { label: "Mizoram", value: "Mizoram", iso2: "MZ" },
        { label: "Nagaland", value: "Nagaland", iso2: "NL" },
        { label: "Odisha", value: "Odisha", iso2: "OR" },
        { label: "Punjab", value: "Punjab", iso2: "PB" },
        { label: "Rajasthan", value: "Rajasthan", iso2: "RJ" },
        { label: "Sikkim", value: "Sikkim", iso2: "SK" },
        { label: "Tamil Nadu", value: "Tamil Nadu", iso2: "TN" },
        { label: "Telangana", value: "Telangana", iso2: "TG" },
        { label: "Tripura", value: "Tripura", iso2: "TR" },
        { label: "Uttar Pradesh", value: "Uttar Pradesh", iso2: "UP" },
        { label: "Uttarakhand", value: "Uttarakhand", iso2: "UT" },
        { label: "West Bengal", value: "West Bengal", iso2: "WB" },
        { label: "Andaman and Nicobar Islands", value: "Andaman and Nicobar Islands", iso2: "AN" },
        { label: "Chandigarh", value: "Chandigarh", iso2: "CH" },
        { label: "Dadra and Nagar Haveli and Daman and Diu", value: "Dadra and Nagar Haveli and Daman and Diu", iso2: "DH" },
        { label: "Delhi", value: "Delhi", iso2: "DL" },
        { label: "Jammu and Kashmir", value: "Jammu and Kashmir", iso2: "JK" },
        { label: "Ladakh", value: "Ladakh", iso2: "LA" },
        { label: "Lakshadweep", value: "Lakshadweep", iso2: "LD" },
        { label: "Puducherry", value: "Puducherry", iso2: "PY" },
    ];
    const [selectedState, setSelectedState] = useState("");
    const [states, setStates] = useState(INDIAN_STATES);
    const [cities, setCities] = useState([]);

    // Operating Hours State
    const days = [
        { label: 'Monday', value: 1 },
        { label: 'Tuesday', value: 2 },
        { label: 'Wednesday', value: 3 },
        { label: 'Thursday', value: 4 },
        { label: 'Friday', value: 5 },
        { label: 'Saturday', value: 6 },
        { label: 'Sunday', value: 7 },
    ];

    const emptySchedule = { 1: [], 2: [], 3: [], 4: [], 5: [], 6: [], 7: [] };
    const allClosed = { 1: true, 2: true, 3: true, 4: true, 5: true, 6: true, 7: true };

    const [schedule, setSchedule] = useState(emptySchedule);
    const [closedDays, setClosedDays] = useState(allClosed);
    const [operatingHoursExist, setOperatingHoursExist] = useState(false);
    const [addressSaved, setAddressSaved] = useState(Boolean(routeWarehouseId));

    // ----------------------------------------------------
    // Fulfillments State & Handlers
    // ----------------------------------------------------
    const [fulfillmentList, setFulfillmentList] = useState([]);
    const [isFulfillmentLoading, setIsFulfillmentLoading] = useState(false);
    const [showFulfillmentForm, setShowFulfillmentForm] = useState(false);
    const [fulfillmentEditId, setFulfillmentEditId] = useState(null);
    const initialFulfillmentData = {
        fulfillment_type: "",
        fulfillment_method: "",
        delivery_fee: 0,
        packing_charge: 0,
        miscellaneous_charge: 0,
    };
    const [fulfillmentForm, setFulfillmentForm] = useState(initialFulfillmentData);
    const [fulfillmentErrors, setFulfillmentErrors] = useState({});

    const fetchFulfillments = async (targetWhId) => {
        const whId = targetWhId || effectiveWarehouseId || selectedTimingWarehouseId || routeWarehouseId;
        if (!whId) {
            setFulfillmentList([]);
            return;
        }
        try {
            setIsFulfillmentLoading(true);
            const token = sessionStorage.getItem("token") || rawToken;
            const response = await axios.get(
                `${apiEndpoint}/snp/warehouse/fulfillments/get/${whId}`,
                {
                    headers: {
                        "Content-Type": "application/json",
                        "x-custom-authorization": `Bearer ${token}`,
                    }
                }
            );
            const resData = response?.data?.data || response?.data?.fulfillments || response?.data || [];
            setFulfillmentList(Array.isArray(resData) ? resData : (resData ? [resData] : []));
        } catch (err) {
            console.log("Fetch Fulfillments Error:", err);
            setFulfillmentList([]);
        } finally {
            setIsFulfillmentLoading(false);
        }
    };

    const fetchFulfillmentById = async (fulfillmentId) => {
        try {
            setIsFulfillmentLoading(true);
            const token = sessionStorage.getItem("token") || rawToken;
            const response = await axios.get(
                `${apiEndpoint}/snp/warehouse/fulfillments/get-by-id/${fulfillmentId}`,
                {
                    headers: {
                        "Content-Type": "application/json",
                        "x-custom-authorization": `Bearer ${token}`,
                    }
                }
            );
            const record = response?.data?.data?.[0] || response?.data?.data || {};
            setFulfillmentForm({
                fulfillment_type: record.fulfillment_type || "",
                fulfillment_method: record.fulfillment_method || "",
                delivery_fee: record.delivery_fee || 0,
                packing_charge: record.packing_charge || 0,
                miscellaneous_charge: record.miscellaneous_charge || 0,
            });
            setFulfillmentEditId(fulfillmentId);
            setShowFulfillmentForm(true);
        } catch (err) {
            console.log("Get Fulfillment By ID Error:", err);
            toast.error("Failed to load fulfillment details");
        } finally {
            setIsFulfillmentLoading(false);
        }
    };

    // const deleteFulfillment = async (fulfillmentId) => {
    //     if (!fulfillmentId) {
    //         toast.error("Invalid Fulfillment ID!");
    //         return;
    //     }
    //     if (!window.confirm("Are you sure you want to delete this fulfillment configuration?")) {
    //         return;
    //     }
    //     try {
    //         setIsFulfillmentLoading(true);
    //         const token = sessionStorage.getItem("token") || rawToken;
    //         const response = await axios({
    //             url: `${apiEndpoint}/snp/warehouse/fulfillments/delete/${fulfillmentId}`,
    //             method: "DELETE",
    //             headers: {
    //                 "Content-Type": "application/json",
    //                 "X-Custom-Authorization": `Bearer ${token}`,
    //             }
    //         });

    //         if (response?.data?.status === true || response?.data?.status === 200 || response?.data?.success) {
    //             toast.success(response?.data?.message || "Fulfillment configuration deleted successfully!");
    //             const whId = effectiveWarehouseId || selectedTimingWarehouseId || routeWarehouseId;
    //             fetchFulfillments(whId);
    //         } else {
    //             toast.error(response?.data?.message || "Failed to delete fulfillment configuration.");
    //         }
    //     } catch (err) {
    //         console.error("Delete fulfillment error:", err);
    //         toast.error(err?.response?.data?.message || err?.message || "Failed to delete fulfillment configuration.");
    //     } finally {
    //         setIsFulfillmentLoading(false);
    //     }
    // };

    const handleToggleFulfillmentStatus = async (fulfillmentId, currentStatus) => {
        if (!fulfillmentId) {
            toast.error("Invalid ID!");
            return;
        }
        const newStatus = !currentStatus;
        try {
            setTogglingFulfillmentId(fulfillmentId);
            const token = sessionStorage.getItem("token") || rawToken;
            const response = await axios({
                url: `${apiEndpoint}/snp/warehouse/fulfillments/enable-disable/${fulfillmentId}`,
                method: "PUT",
                headers: {
                    "Content-Type": "application/json",
                    "x-custom-authorization": `Bearer ${token}`,
                },
                data: {
                    is_active: newStatus,
                    status: newStatus,
                },
            });

            const resData = response?.data;
            const isSuccess = resData?.status === true || resData?.status === 200 || resData?.success === true || response?.status === 200;

            if (isSuccess) {
                const rawVal = resData?.data?.is_active !== undefined ? resData.data.is_active : (resData?.data?.status !== undefined ? resData.data.status : newStatus);
                const apiReturnedActive = rawVal === true || rawVal === 1 || rawVal === "1" || rawVal === "true";

                toast.success(resData?.message || `Fulfillment status updated successfully!`);

                setFulfillmentList(prev => prev.map(item =>
                    (item.id === fulfillmentId || item.fulfillment_id === fulfillmentId || item.id === resData?.data?.id)
                        ? { ...item, is_active: apiReturnedActive, status: apiReturnedActive }
                        : item
                ));

                setServiceabilityList(prev => prev.map(item =>
                    (item.id === fulfillmentId || item.fulfillment_id === fulfillmentId || item.warehouse_location_id === resData?.data?.warehouse_location_id || item.id === resData?.data?.id)
                        ? { ...item, is_active: apiReturnedActive, status: apiReturnedActive }
                        : item
                ));
            } else {
                toast.error(resData?.message || "Failed to update status.");
            }
        } catch (err) {
            console.error("Toggle status error:", err);
            toast.error(err?.response?.data?.message || err?.message || "Failed to update status.");
        } finally {
            setTogglingFulfillmentId(null);
        }
    };

    const saveFulfillment = async () => {
        const { fulfillment_type, fulfillment_method, delivery_fee, packing_charge, miscellaneous_charge } = fulfillmentForm;

        const errors = {};
        if (!fulfillment_type) {
            errors.fulfillment_type = "Please select Fulfillment Type!";
        }
        if (!fulfillment_method) {
            errors.fulfillment_method = "Please select Fulfillment Method!";
        }

        const dFee = Number(delivery_fee);
        if (isNaN(dFee) || dFee < 0) {
            errors.delivery_fee = "Delivery fee cannot be negative!";
        }
        const pCharge = Number(packing_charge);
        if (isNaN(pCharge) || pCharge < 0) {
            errors.packing_charge = "Packing charge cannot be negative!";
        }
        const mCharge = Number(miscellaneous_charge);
        if (isNaN(mCharge) || mCharge < 0) {
            errors.miscellaneous_charge = "Miscellaneous charge cannot be negative!";
        }

        if (Object.keys(errors).length > 0) {
            setFulfillmentErrors(errors);
            if (errors.fulfillment_type) {
                toast.error(errors.fulfillment_type);
            } else if (errors.fulfillment_method) {
                toast.error(errors.fulfillment_method);
            } else {
                toast.error(Object.values(errors)[0]);
            }
            return;
        }

        setFulfillmentErrors({});

        const whId = effectiveWarehouseId || selectedTimingWarehouseId || routeWarehouseId;
        if (!whId) {
            toast.error("Please create/save warehouse first!");
            return;
        }

        try {
            setIsFulfillmentLoading(true);
            const token = sessionStorage.getItem("token") || rawToken;
            const payload = {
                warehouse_id: String(whId),
                fulfillment_configs: [
                    {
                        fulfillment_type,
                        fulfillment_method,
                        is_active: true,
                        charges: {
                            delivery_fee: Number(delivery_fee || 0),
                            packing_charge: Number(packing_charge || 0),
                            miscellaneous_charge: Number(miscellaneous_charge || 0)
                        }
                    }
                ]
            };

            let response;
            if (fulfillmentEditId) {
                response = await axios.put(
                    `${apiEndpoint}/snp/warehouse/fulfillments/update/${fulfillmentEditId}`,
                    payload,
                    {
                        headers: {
                            "Content-Type": "application/json",
                            "x-custom-authorization": `Bearer ${token}`,
                        }
                    }
                );
                toast.success(response?.data?.message || "Fulfillment updated successfully");
            } else {
                response = await axios.post(
                    `${apiEndpoint}/snp/warehouse/fulfillments/create`,
                    payload,
                    {
                        headers: {
                            "Content-Type": "application/json",
                            "x-custom-authorization": `Bearer ${token}`,
                        }
                    }
                );
                toast.success(response?.data?.message || "Fulfillment created successfully");
            }

            setShowFulfillmentForm(false);
            setFulfillmentEditId(null);
            setFulfillmentForm(initialFulfillmentData);
            fetchFulfillments(whId);
        } catch (err) {
            console.error("Save Fulfillment Error:", err);
            toast.error(err?.response?.data?.message || "Failed to save fulfillment");
        } finally {
            setIsFulfillmentLoading(false);
        }
    };

    // ----------------------------------------------------
    // Serviceability State & Handlers
    // ----------------------------------------------------
    const [serviceabilityList, setServiceabilityList] = useState([]);
    const [hasServiceability, setHasServiceability] = useState(false);
    const [isServiceabilityLoading, setIsServiceabilityLoading] = useState(false);
    const [showServiceabilityForm, setShowServiceabilityForm] = useState(false);
    const [serviceabilityCities, setServiceabilityCities] = useState([]);
    const [isCitiesLoading, setIsCitiesLoading] = useState(false);
    const [serviceabilityWarehouseTypes, setServiceabilityWarehouseTypes] = useState([]);
    const [userChangedHyperCity, setUserChangedHyperCity] = useState(false);
    const [serviceFeature, setServiceFeature] = useState("");
    const [serviceData, setServiceData] = useState({
        service_type: "",
        service_range: "",
        service_city: "",
        served_city_list: []
    });
    const [selectedServiceCities, setSelectedServiceCities] = useState([]);
    const [serviceabilityEditId, setServiceabilityEditId] = useState(null);

    const cityOptions = useMemo(() => {
        const options = (serviceabilityCities || []).map((item) => ({
            value: item.display_name || item.city_code,
            label: item.display_name ? `${item.display_name}${item.city_code ? ` (${item.city_code})` : ''}` : item.city_code,
        }));
        if (Array.isArray(serviceData?.served_city_list)) {
            serviceData.served_city_list.forEach((city) => {
                if (city && !options.some((o) => o.value === city)) {
                    options.push({ value: city, label: city });
                }
            });
        }
        if (serviceData?.service_city && !options.some((o) => o.value === serviceData.service_city)) {
            options.push({ value: serviceData.service_city, label: serviceData.service_city });
        }
        return options;
    }, [serviceabilityCities, serviceData.served_city_list, serviceData.service_city]);

    const fetchServiceabilityData = async (targetWhId) => {
        const whId = targetWhId || effectiveWarehouseId || selectedTimingWarehouseId;
        if (!whId) {
            setServiceabilityList([]);
            return;
        }

        try {
            setIsServiceabilityLoading(true);
            const token = sessionStorage.getItem("token") || rawToken;
            const response = await axios({
                url: `${apiEndpoint}/snp/warehouse/serviceability/get/${whId}`,
                method: "GET",
                headers: {
                    "x-custom-authorization": `Bearer ${token}`,
                },
            });

            const resData = response?.data;
            const isStatusTrue = Boolean(resData?.status === true || resData?.status === 200 || resData?.success);
            let tableData = resData?.data || resData?.serviceability || resData?.data?.serviceability || [];
            if (!Array.isArray(tableData)) {
                if (typeof tableData === 'object' && tableData !== null) {
                    tableData = [tableData];
                } else {
                    tableData = [];
                }
            }
            setServiceabilityList(tableData);
            setHasServiceability(isStatusTrue && tableData.length > 0);

            if (tableData.length > 0) {
                const firstRecord = tableData[0];
                setServiceFeature(firstRecord?.feature || firstRecord?.warehouse_type || "");
                const hyperCity = Array.isArray(firstRecord?.service_city)
                    ? (firstRecord.service_city[0] || "")
                    : (firstRecord?.service_city || "");
                const servedList = Array.isArray(firstRecord?.served_city_list)
                    ? firstRecord.served_city_list
                    : (firstRecord?.served_city_list ? [firstRecord.served_city_list] : []);
                setServiceData({
                    service_type: firstRecord?.service_type || "hyperlocal",
                    service_range: firstRecord?.service_range || "",
                    service_city: hyperCity,
                    served_city_list: servedList
                });
                if (firstRecord?.service_type === "intercity") {
                    const interCities = (firstRecord?.served_city_list && firstRecord.served_city_list.length > 0)
                        ? firstRecord.served_city_list
                        : firstRecord?.service_city;
                    if (Array.isArray(interCities)) {
                        setSelectedServiceCities(interCities.map(s => String(s).trim()));
                    } else if (interCities) {
                        setSelectedServiceCities(String(interCities).split(",").map((s) => s.trim()));
                    }
                }
            }
        } catch (error) {
            console.log("Fetch Serviceability Error:", error);
            setServiceabilityList([]);
            setHasServiceability(false);
        } finally {
            setIsServiceabilityLoading(false);
        }
    };

    const fetchServiceabilityCities = async () => {
        // Agar already fetch ho chuki hai, dobara mat karo
        if (serviceabilityCities.length > 0) return;

        try {
            setIsCitiesLoading(true);
            const token = sessionStorage.getItem("token") || rawToken;
            const { data } = await axios({
                url: `${apiEndpoint}/snp/warehouse/getcities`,
                method: "GET",
                headers: {
                    "x-custom-authorization": `Bearer ${token}`,
                }
            });
            if (data.status === true || data.status === 200) {
                setServiceabilityCities(data.Cities || []);
            }
        } catch (err) {
            console.log("Get Cities Error:", err);
        } finally {
            setIsCitiesLoading(false);
        }
    };
    const fetchServiceabilityWarehouseTypes = async (targetWhId) => {
        const whId = targetWhId || effectiveWarehouseId || selectedTimingWarehouseId || routeWarehouseId;
        if (!whId) return;
        try {
            const token = sessionStorage.getItem("token") || rawToken;
            const response = await axios({
                method: "GET",
                url: `${apiEndpoint}/snp/warehouse/serviceability/get/${whId}`,
                headers: {
                    "Content-Type": "application/json",
                    "X-Custom-Authorization": `Bearer ${token}`,
                },
            });
            if (response.status === true || response.data?.status === true || response.status === 200) {
                const resList = response.data?.data || response.data?.serviceability || [];
                const list = Array.isArray(resList) ? resList : (resList ? [resList] : []);
                setServiceabilityWarehouseTypes(list.map(
                    item => ({ label: item?.feature || item?.warehouse_type || item?.warehouse_name, id: item?.id })
                ));
            }
        } catch (error) {
            console.log("Get Warehouse Types Error:", error);
        }
    };

    const deleteServiceability = async (serviceabilityId) => {
        if (!serviceabilityId) {
            toast.error("Invalid Serviceability ID!");
            return;
        }
        if (!window.confirm("Are you sure you want to delete this serviceability configuration?")) {
            return;
        }
        try {
            setIsServiceabilityLoading(true);
            const token = sessionStorage.getItem("token") || rawToken;
            const response = await axios({
                url: `${apiEndpoint}/snp/warehouse/serviceability/delete/${serviceabilityId}`,
                method: "DELETE",
                headers: {
                    "Content-Type": "application/json",
                    "X-Custom-Authorization": `Bearer ${token}`,
                }
            });

            if (response?.data?.status === true || response?.data?.status === 200 || response?.data?.success) {
                toast.success(response?.data?.message || "Serviceability configuration deleted successfully!");
                const whId = effectiveWarehouseId || selectedTimingWarehouseId || routeWarehouseId;
                fetchServiceabilityData(whId);
            } else {
                toast.error(response?.data?.message || "Failed to delete serviceability configuration.");
            }
        } catch (err) {
            console.error("Delete serviceability error:", err);
            toast.error(err?.response?.data?.message || err?.message || "Failed to delete serviceability configuration.");
        } finally {
            setIsServiceabilityLoading(false);
        }
    };

    const saveServiceability = async () => {
        const whId = effectiveWarehouseId || selectedTimingWarehouseId;

        if (!whId) {
            toast.error("Please create/save warehouse first!");
            return;
        }

        if (!serviceData.service_type) {
            toast.error("Please select a Serviceability Type!");
            return;
        }

        if (serviceData.service_type === "hyperlocal") {
            const rangeNum = Number(serviceData.service_range);
            if (!serviceData.service_range || isNaN(rangeNum) || rangeNum <= 0) {
                toast.error("Please enter a valid positive area range in Km (greater than 0)!");
                return;
            }
            if (rangeNum > 100) {
                toast.error("Area range cannot be greater than 100 Km!");
                return;
            }
        }

        try {
            setIsServiceabilityLoading(true);
            let cityList = "";
            if (serviceData.service_type === "hyperlocal") {
                if (userChangedHyperCity && serviceData.service_city) {
                    // User explicitly picked a new city from dropdown
                    cityList = String(serviceData.service_city);
                } else if (Array.isArray(serviceData.served_city_list) && serviceData.served_city_list.length > 0) {
                    // User did NOT change city → send ALL existing cities
                    cityList = serviceData.served_city_list.join(",");
                } else {
                    cityList = "";
                }
            } else if (serviceData.service_type === "intercity") {
                cityList = Array.isArray(selectedServiceCities)
                    ? selectedServiceCities.filter(Boolean).map(c => String(c))
                    : (selectedServiceCities ? String(selectedServiceCities) : "");
            } else if (serviceData.service_type === "pan-india") {
                cityList = "";
            }

            let res;
            const token = sessionStorage.getItem("token") || rawToken;
            if (serviceabilityEditId) {
                // UPDATE existing serviceability
                const updatePayload = {
                    service_type: serviceData.service_type,
                    service_range: String(serviceData.service_range),
                    service_city: cityList
                };
                res = await axios({
                    url: `${apiEndpoint}/snp/warehouse/serviceability/update/${serviceabilityEditId}`,
                    method: "PUT",
                    headers: {
                        "Content-Type": "application/json",
                        "X-Custom-Authorization": `Bearer ${token}`,
                    },
                    data: updatePayload
                });
            } else {
                // CREATE new serviceability
                const createPayload = {
                    warehouse_id: String(whId),
                    service_type: serviceData.service_type,
                    service_range: String(serviceData.service_range),
                    service_city: cityList
                };
                res = await axios({
                    url: `${apiEndpoint}/snp/warehouse/serviceability/create`,
                    method: "POST",
                    headers: {
                        "Content-Type": "application/json",
                        "X-Custom-Authorization": `Bearer ${token}`,
                    },
                    data: createPayload
                });
            }

            if (res?.data?.status === true || res?.data?.status === 200 || res?.status === 200 || res?.data?.success) {
                toast.success(res?.data?.message || (serviceabilityEditId ? "Serviceability Updated Successfully" : "Serviceability Created Successfully"));
                setShowServiceabilityForm(false);
                setServiceData({ service_type: "", service_range: "", service_city: "", served_city_list: [] });
                setSelectedServiceCities([]);
                setServiceabilityEditId(null);
                fetchServiceabilityData(whId);
            } else {
                toast.error(res?.data?.message || (serviceabilityEditId ? "Failed to update serviceability" : "Failed to create serviceability"));
            }
        } catch (err) {
            toast.error(err?.response?.data?.message || "Something went wrong, please try later!");
        } finally {
            setIsServiceabilityLoading(false);
        }
    };

    // Fetch States
    async function getStatesByCountry() {
        const key = "b4e03d17041262a06649f033f19da4f863d03734431444b7c4740991c86dff44";
        try {
            const response = await axios({
                url: `https://api.countrystatecity.in/v1/countries/IN/states`,
                method: "GET",
                headers: {
                    "X-CSCAPI-KEY": key,
                },
            });
            const stateOptions = (response?.data || []).map(state => ({
                label: state.name,
                value: state.name,
                iso2: state.iso2,
            }));
            if (stateOptions.length > 0) {
                setStates(stateOptions);
            }
        } catch (error) {
            console.error("Get States Error --->", error);
            setStates(INDIAN_STATES);
        }
    }

    // Fetch Cities for State
    async function getCitiesByState(stateIso) {
        const key = "b4e03d17041262a06649f033f19da4f863d03734431444b7c4740991c86dff44";
        try {
            const response = await axios({
                url: `https://api.countrystatecity.in/v1/countries/IN/states/${stateIso}/cities`,
                method: "GET",
                headers: {
                    "X-CSCAPI-KEY": key,
                },
            });
            const cityOptions = (response?.data || []).map(city => ({
                label: city.name,
                value: city.name,
            }));
            setCities(cityOptions);
        } catch (error) {
            console.error("Get Cities Error --->", error);
        }
    }

    const handleStateChange = (value, option) => {
        setSelectedState(value);
        form.setFieldsValue({ state: value, city: undefined });
        const iso = option?.iso2 || states.find(s => s.value === value)?.iso2;
        if (iso) {
            getCitiesByState(iso);
        }
    };

    // Auto-fill state and city from pincode using postalpincode.in
    const handlePincodeChange = async (e) => {
        const rawVal = e?.target ? e.target.value : e;
        const pincode = (rawVal || "").toString().replace(/\D/g, "").slice(0, 6);
        if (!pincode || pincode.length < 6) {
            setPincodeStatus(null);
            setIsPincodeLoading(false);
            form.setFieldsValue({ state: undefined, city: undefined });
            setSelectedState("");
            setCities([]);
            if (!effectiveWarehouseId) {
                const currentVals = form.getFieldsValue();
                const draftData = {
                    ...currentVals,
                    area_code: pincode || undefined,
                    state: undefined,
                    city: undefined,
                    citiesList: []
                };
                sessionStorage.setItem("add_warehouse_draft", JSON.stringify(draftData));
                localStorage.setItem("add_warehouse_draft", JSON.stringify(draftData));
            }
            return;
        }
        if (!/^[0-9]{6}$/.test(pincode)) {
            setPincodeStatus("error");
            form.setFieldsValue({ state: undefined, city: undefined });
            setSelectedState("");
            setCities([]);
            return;
        }
        try {
            setIsPincodeLoading(true);
            setPincodeStatus("loading");
            const res = await axios.get(`https://api.postalpincode.in/pincode/${pincode}`);
            const resultObj = res?.data?.[0];
            const postOffices = resultObj?.PostOffice;
            const status = resultObj?.Status;

            if (status === "Error" || !Array.isArray(postOffices) || postOffices.length === 0) {
                setPincodeStatus("error");
                form.setFieldsValue({ state: undefined, city: undefined });
                setSelectedState("");
                setCities([]);
                toast.error(resultObj?.Message || "No records found for this pincode.");
                return;
            }

            if (status === "Success" || (Array.isArray(postOffices) && postOffices.length > 0)) {
                setPincodeStatus("success");
            }

            const firstPost = postOffices[0];
            const stateName = firstPost?.State || "";
            const districtName = firstPost?.District || firstPost?.Block || firstPost?.Name || "";

            // Extract all unique cities/localities from postal API response
            const uniquePlaces = new Set();
            if (districtName) uniquePlaces.add(districtName);
            postOffices.forEach(po => {
                if (po?.District) uniquePlaces.add(po.District);
                if (po?.Block && po.Block !== "NA") uniquePlaces.add(po.Block);
                if (po?.Name) uniquePlaces.add(po.Name);
            });

            const cityOptions = Array.from(uniquePlaces).map(name => ({
                label: name,
                value: name
            }));

            // Ensure state exists in states dropdown
            setStates(prev => {
                const existing = prev.length > 0 ? prev : INDIAN_STATES;
                if (!existing.some(s => s.value.toLowerCase() === stateName.toLowerCase())) {
                    return [{ label: stateName, value: stateName }, ...existing];
                }
                return existing;
            });

            setCities(cityOptions);
            setSelectedState(stateName);
            form.setFieldsValue({
                state: stateName,
                city: districtName
            });

            // Save to draft storage
            if (!effectiveWarehouseId) {
                const currentVals = form.getFieldsValue();
                const draftData = {
                    ...currentVals,
                    area_code: pincode,
                    state: stateName,
                    city: districtName,
                    citiesList: cityOptions
                };
                sessionStorage.setItem("add_warehouse_draft", JSON.stringify(draftData));
                localStorage.setItem("add_warehouse_draft", JSON.stringify(draftData));
            }

            // Optional: merge with state cities from CSC API
            const matchedState = (states.length > 0 ? states : INDIAN_STATES).find(s =>
                s.label.toLowerCase() === stateName.toLowerCase() ||
                s.value.toLowerCase() === stateName.toLowerCase()
            );

            if (matchedState?.iso2) {
                const key = "b4e03d17041262a06649f033f19da4f863d03734431444b7c4740991c86dff44";
                try {
                    const cityRes = await axios({
                        url: `https://api.countrystatecity.in/v1/countries/IN/states/${matchedState.iso2}/cities`,
                        method: "GET",
                        headers: { "X-CSCAPI-KEY": key },
                        timeout: 3000
                    });
                    const cscOptions = (cityRes?.data || []).map(city => ({
                        label: city.name,
                        value: city.name,
                    }));
                    if (cscOptions.length > 0) {
                        const merged = [...cityOptions];
                        cscOptions.forEach(c => {
                            if (!merged.some(item => item.value.toLowerCase() === c.value.toLowerCase())) {
                                merged.push(c);
                            }
                        });
                        setCities(merged);
                    }
                } catch (cityErr) {
                    console.log("CSC City lookup error:", cityErr?.message);
                }
            }
        } catch (err) {
            console.log("Pincode Lookup Error:", err);
            setPincodeStatus("error");
        } finally {
            setIsPincodeLoading(false);
        }
    };

    // Restore Draft from Storage
    const restoreDraft = async (draft) => {
        if (!draft || typeof draft !== "object") return;
        form.setFieldsValue(draft);
        if (draft.state) {
            setSelectedState(draft.state);
        }
        if (draft.citiesList && Array.isArray(draft.citiesList) && draft.citiesList.length > 0) {
            setCities(draft.citiesList);
        } else if (draft.city) {
            setCities([{ label: draft.city, value: draft.city }]);
        }
        if (draft.area_code && /^[0-9]{6}$/.test(draft.area_code)) {
            setPincodeStatus("success");
            if (!draft.state || !draft.city) {
                handlePincodeChange(draft.area_code);
            }
        }
    };

    // Geocoding Helper
    const getLatLng = async (pincode) => {
        try {
            const res = await axios.get(`https://nominatim.openstreetmap.org/search?postalcode=${pincode}&country=India&format=json`);
            if (res.data && res.data.length > 0) {
                return {
                    lat: res.data[0].lat,
                    lng: res.data[0].lon
                };
            }
            return { lat: "", lng: "" };
        } catch (error) {
            console.error("Geocoding Error --->", error);
            return { lat: "", lng: "" };
        }
    };

    // Fetch Warehouse list for selector
    const fetchWarehouseList = async () => {
        try {
            const token = sessionStorage.getItem("token") || rawToken;
            const response = await axios.get(`${apiEndpoint}/snp/warehouse/get`, {
                headers: {
                    "Content-Type": "application/json",
                    "x-custom-authorization": `Bearer ${token}`,
                }
            });
            const list = response?.data?.data || response?.data || [];
            if (Array.isArray(list)) {
                setWarehouseList(list);
            }
        } catch (error) {
            console.log("Fetch Warehouse List Error --->", error);
        }
    };

    // Populate Operating Schedule helper
    const populateOperatingSchedule = (hoursData) => {
        if (!hoursData) {
            setOperatingHoursExist(false);
            setSchedule(emptySchedule);
            setClosedDays(allClosed);
            return;
        }

        let list = [];
        if (Array.isArray(hoursData)) {
            list = hoursData;
        } else if (Array.isArray(hoursData?.operating_days)) {
            list = hoursData.operating_days;
        } else if (Array.isArray(hoursData?.operating_hours)) {
            list = hoursData.operating_hours;
        } else if (Array.isArray(hoursData?.location?.[0]?.operating_days)) {
            list = hoursData.location[0].operating_days;
        }

        if (list.length > 0) {
            setOperatingHoursExist(true);
            const newSchedule = { 1: [], 2: [], 3: [], 4: [], 5: [], 6: [], 7: [] };
            const newClosedDays = { 1: true, 2: true, 3: true, 4: true, 5: true, 6: true, 7: true };

            list.forEach(item => {
                const dayNum = Number(item.day_of_week);
                const isClosed = Boolean(item.is_closed);
                newClosedDays[dayNum] = isClosed;

                const rawShifts = item.shifts || item.shift || (Array.isArray(item) ? item : []);
                if (!isClosed && Array.isArray(rawShifts) && rawShifts.length > 0) {
                    newSchedule[dayNum] = rawShifts.map((shift) => {
                        const openStr = shift.opening_time ? String(shift.opening_time).slice(0, 5) : "09:00";
                        const closeStr = shift.closing_time ? String(shift.closing_time).slice(0, 5) : "18:00";
                        const extractedId = shift.id !== undefined && shift.id !== null ? shift.id : (shift.shift_id !== undefined ? shift.shift_id : (shift.shiftId || shift._id || null));
                        return {
                            id: extractedId,
                            operating_day_id: item.id || item.operating_day_id || null,
                            times: [dayjs(openStr, "HH:mm"), dayjs(closeStr, "HH:mm")]
                        };
                    });
                } else {
                    newSchedule[dayNum] = [];
                }
            });

            setClosedDays(newClosedDays);
            setSchedule(newSchedule);
        } else {
            setOperatingHoursExist(false);
            setSchedule(emptySchedule);
            setClosedDays(allClosed);
        }
    };

    // Fetch Single Warehouse Details for editing
    const fetchWarehouseDetails = async (warehouseId) => {
        if (!warehouseId) return;
        try {
            setIsLoading(true);
            const token = sessionStorage.getItem("token") || rawToken;
            const response = await axios.get(`${apiEndpoint}/snp/warehouse/get/${warehouseId}`, {
                headers: {
                    "Content-Type": "application/json",
                    "x-custom-authorization": `Bearer ${token}`,
                }
            });

            const resData = response?.data?.data?.[0] || response?.data?.data || response?.data;
            const wh = Array.isArray(resData?.warehouse) ? (resData.warehouse[0] || {}) : (resData?.warehouse || resData || {});
            const loc = Array.isArray(resData?.location) ? (resData.location[0] || {}) : (resData?.location || {});
            const opDays = resData?.operating_days || resData?.operatingDays || loc?.operating_days || loc?.operatingDays || (Array.isArray(resData?.location) ? resData.location[0]?.operating_days : []) || [];
            const bankAcc = resData?.bank_account;

            if (wh || loc) {
                const streetVal = loc.street || wh.street || "";
                const areaCodeVal = loc.area_code || loc.pincode || wh.area_code || wh.pincode || "";
                const localityVal = loc.locality || wh.locality || "";
                const stateVal = loc.state || wh.state || "";
                const cityVal = loc.city || wh.city || "";

                // Reset form first so any unsaved/dirty inputs typed by user are completely cleared
                form.resetFields();

                if (cityVal) {
                    setCities(prev => {
                        if (Array.isArray(prev) && prev.some(c => (c.value || "").toLowerCase() === String(cityVal).toLowerCase())) {
                            return prev;
                        }
                        return [{ label: cityVal, value: cityVal }, ...(Array.isArray(prev) ? prev : [])];
                    });
                }

                form.setFieldsValue({
                    warehouse_name: wh.warehouse_name || "",
                    warehouse_code: wh.warehouse_code || "",
                    warehouse_contact_no: wh.warehouse_contact_no || "",
                    email: wh.email || "",
                    fssai_license_no: wh.fssai_license_no || "",
                    street: streetVal,
                    area_code: areaCodeVal,
                    locality: localityVal,
                    state: stateVal,
                    city: cityVal,
                    status: wh.status !== undefined ? Boolean(wh.status) : true,
                    network_logistics: wh.network_logistics !== undefined ? Boolean(wh.network_logistics) : false,
                    ondc_commission: wh.ondc_commission !== undefined && wh.ondc_commission !== null ? wh.ondc_commission : ""
                });
                setSelectedState(stateVal);
                if (areaCodeVal && /^[0-9]{6}$/.test(areaCodeVal)) {
                    setPincodeStatus("success");
                } else {
                    setPincodeStatus(null);
                }
                setAddressSaved(true);
                setEffectiveWarehouseId(Number(warehouseId));
                setSelectedTimingWarehouseId(Number(warehouseId));
                if (wh.location_id || loc.location_id || loc.id) {
                    setEffectiveLocationId(wh.location_id || loc.location_id || loc.id);
                }
                if (bankAcc) {
                    setBankDetails(bankAcc);
                }

                if (Array.isArray(opDays) && opDays.length > 0) {
                    populateOperatingSchedule(opDays);
                }
            }
        } catch (error) {
            console.error("Fetch Warehouse Details Error --->", error);
        } finally {
            setIsLoading(false);
        }
    };

    useEffect(() => {
        getStatesByCountry();
        fetchWarehouseList();
        fetchServiceabilityCities();
        fetchServiceabilityWarehouseTypes();
    }, []);

    // When route warehouse ID changes (Edit vs Add mode)
    useEffect(() => {
        if (routeWarehouseId) {
            fetchWarehouseDetails(routeWarehouseId);
            setEffectiveWarehouseId(Number(routeWarehouseId));
            setSelectedTimingWarehouseId(Number(routeWarehouseId));
            fetchOperatingHours(routeWarehouseId);
            fetchServiceabilityData(routeWarehouseId);
            fetchFulfillments(routeWarehouseId);
            fetchBankDetails(routeWarehouseId);
        } else {
            // Fresh Add Warehouse mode - restore from sessionStorage / localStorage draft if available
            const savedDraft = sessionStorage.getItem("add_warehouse_draft") || localStorage.getItem("add_warehouse_draft");
            if (savedDraft) {
                try {
                    const parsedDraft = JSON.parse(savedDraft);
                    restoreDraft(parsedDraft);
                } catch (e) {
                    console.error("Failed to parse warehouse draft:", e);
                }
            } else {
                form.resetFields();
                form.setFieldsValue({ status: true });
                setSelectedState("");
            }
            setEffectiveWarehouseId(null);
            setEffectiveLocationId(null);
            setSelectedTimingWarehouseId(null);
            setSchedule(emptySchedule);
            setClosedDays(allClosed);
            setOperatingHoursExist(false);
            setAddressSaved(false);
            setServiceabilityList([]);
            setHasServiceability(false);
            setFulfillmentList([]);
            setShowServiceabilityForm(false);
            setShowFulfillmentForm(false);
        }
    }, [routeWarehouseId, routeLocationId]);

    useEffect(() => {
        const path = (location.pathname || "").toLowerCase();
        const currentParams = new URLSearchParams(location.search);
        const whKey = routeWarehouseId || effectiveWarehouseId || selectedTimingWarehouseId || "default";
        const savedTab = sessionStorage.getItem(`active_wh_tab_${whKey}`) || sessionStorage.getItem("active_warehouse_tab");

        const tabFromUrl = currentParams.get("tab") || (path.includes("serviceability") ? "4" : (path.includes("fulfillment") ? "3" : (savedTab || initialTab || "1")));
        if (tabFromUrl && tabFromUrl !== activeTab) {
            setActiveTab(tabFromUrl);
        }
        sessionStorage.setItem(`active_wh_tab_${whKey}`, tabFromUrl);
        sessionStorage.setItem("active_warehouse_tab", tabFromUrl);
    }, [location.pathname, location.search, routeWarehouseId, effectiveWarehouseId, initialTab]);

    useEffect(() => {
        const currentWhId = effectiveWarehouseId || selectedTimingWarehouseId || routeWarehouseId;
        const prevTab = prevTabRef.current;
        prevTabRef.current = activeTab;

        if (activeTab === "1") {
            if (currentWhId && prevTab !== "1") {
                fetchWarehouseDetails(currentWhId);
            }
        } else if (activeTab === "2") {
            if (currentWhId && prevTab !== "2") {
                fetchOperatingHours(currentWhId);
            }
        } else if (activeTab === "3") {
            if (currentWhId) {
                fetchFulfillments(currentWhId);
            }
        } else if (activeTab === "4") {
            fetchServiceabilityCities();
            fetchServiceabilityWarehouseTypes(currentWhId);
            if (currentWhId) {
                fetchServiceabilityData(currentWhId);
            }
        } else if (activeTab === "5") {
            if (currentWhId) {
                fetchBankDetails(currentWhId);
            } else {
                setBankDetails(null);
            }
        }
    }, [activeTab, effectiveWarehouseId, selectedTimingWarehouseId, routeWarehouseId]);

    // Handle Warehouse Basic Info Save / Update
    const onFinish = async (values) => {
        const contactNo = String(values.warehouse_contact_no || "").trim();
        if (contactNo.startsWith("0")) {
            toast.error("Contact number cannot start with 0! Please enter a valid 10-digit number.");
            return;
        }
        if (!/^[6-9][0-9]{9}$/.test(contactNo)) {
            toast.error("Please enter a valid 10-digit mobile number starting with 6, 7, 8, or 9!");
            return;
        }

        try {
            setIsLoading(true);
            const { lat, lng } = await getLatLng(values.area_code);

            const isEdit = Boolean(effectiveWarehouseId);
            const payload = {
                ...(isEdit ? { id: Number(effectiveWarehouseId) } : {}),
                warehouse_name: values.warehouse_name,
                warehouse_code: values.warehouse_code,
                warehouse_contact_no: contactNo,
                email: values.email,
                status: values.status !== undefined ? values.status : true,
                fssai_license_no: values.fssai_license_no,
                city: values.city,
                street: values.street,
                area_code: values.area_code,
                state: values.state,
                locality: values.locality || "",
                latitude: lat,
                longitude: lng,
            };

            const url = isEdit
                ? `${apiEndpoint}/snp/warehouse/update/${effectiveWarehouseId}`
                : `${apiEndpoint}/snp/warehouse/create`;

            const method = isEdit ? "PUT" : "POST";

            const response = await axios({
                url,
                method,
                headers: {
                    "Content-Type": "application/json",
                    "x-custom-authorization": `Bearer ${sessionStorage.getItem("token") || rawToken}`,
                },
                data: payload
            });

            if (response?.data?.status === true || response?.data?.status === 200 || response?.data?.success) {
                toast.success(response?.data?.message || (isEdit ? "Warehouse updated successfully!" : "Warehouse created successfully!"));
                setAddressSaved(true);

                const createdData = response?.data?.data;
                const createdWarehouse = Array.isArray(createdData)
                    ? createdData[0]
                    : (createdData?.warehouse || createdData || {});
                const createdId = createdWarehouse?.id || createdWarehouse?.warehouse_id || response?.data?.id || effectiveWarehouseId;
                if (createdId) {
                    setEffectiveWarehouseId(Number(createdId));
                    setSelectedTimingWarehouseId(Number(createdId));
                    sessionStorage.setItem("warehouse_id", String(createdId));
                    await fetchBankDetails(createdId);
                }

                if (!isEdit) {
                    form.resetFields();
                    form.setFieldsValue({ status: true });
                    setSelectedState("");
                    setCities([]);
                }

                fetchWarehouseList();
                sessionStorage.removeItem("add_warehouse_draft");
                localStorage.removeItem("add_warehouse_draft");


                setActiveTab("2");
                const currentParams = new URLSearchParams(location.search);
                currentParams.set("tab", "2");
                if (createdId) {
                    currentParams.set("id", String(createdId));
                }
                const whKey = createdId || "default";
                sessionStorage.setItem(`active_wh_tab_${whKey}`, "2");
                sessionStorage.setItem("active_warehouse_tab", "2");
                router.replace({
                    pathname: location.pathname,
                    search: currentParams.toString()
                });
            } else {
                toast.error(response?.data?.message || "Failed to save warehouse.");
            }
        } catch (error) {
            console.error("Save Warehouse Error --->", error?.response?.data || error?.message);
            toast.error(error?.response?.data?.message || "Failed to save warehouse.");
        } finally {
            setIsLoading(false);
        }
    };

    // Operating Hours helper functions
    const handleDayToggle = (dayValue, checked) => {
        setClosedDays(prev => ({
            ...prev,
            [dayValue]: !checked
        }));

        if (checked && (!schedule[dayValue] || schedule[dayValue].length === 0)) {
            setSchedule(prev => ({
                ...prev,
                [dayValue]: [{ id: null, times: [dayjs("09:00", "HH:mm"), dayjs("18:00", "HH:mm")] }]
            }));
        }
    };

    const [deletingShiftKey, setDeletingShiftKey] = useState(null);

    const handleTimeChange = (dayValue, shiftIndex, time) => {
        setSchedule(prev => {
            const dayShifts = [...(prev[dayValue] || [])];
            const prevShift = dayShifts[shiftIndex];
            dayShifts[shiftIndex] = {
                ...(typeof prevShift === 'object' && !Array.isArray(prevShift) ? prevShift : { id: prevShift?.id || null }),
                times: time ? [time[0], time[1]] : null
            };
            return {
                ...prev,
                [dayValue]: dayShifts
            };
        });
    };

    const addShift = (dayValue) => {
        setSchedule(prev => {
            const dayShifts = [...(prev[dayValue] || [])];
            dayShifts.push({
                id: null,
                times: [dayjs("09:00", "HH:mm"), dayjs("18:00", "HH:mm")]
            });
            return {
                ...prev,
                [dayValue]: dayShifts
            };
        });
    };

    // Delete Shift:
    // If shift has database ID -> call DELETE /snp/warehouse/operating-hours/delete-shifts/:id
    // If newly added unsaved shift -> remove from local state
    const removeShift = async (dayValue, shiftIndex) => {
        const dayShifts = schedule[dayValue] || [];
        const targetShift = dayShifts[shiftIndex];
        const shiftId = targetShift?.id !== undefined && targetShift?.id !== null ? targetShift?.id : (targetShift?.shift_id || targetShift?._id || null);

        console.log(`removeShift -> Day: ${dayValue}, ShiftIndex: ${shiftIndex}, shiftId: ${shiftId}`, targetShift);

        if (shiftId) {
            const shiftKey = `${dayValue}-${shiftIndex}-${shiftId}`;
            try {
                setDeletingShiftKey(shiftKey);
                const token = sessionStorage.getItem("token") || rawToken;
                const response = await axios({
                    url: `${apiEndpoint}/snp/warehouse/operating-hours/delete-shifts/${shiftId}`,
                    method: "DELETE",
                    headers: {
                        "Content-Type": "application/json",
                        "x-custom-authorization": `Bearer ${token}`,
                    }
                });
                console.log("DELETE Shift Response ->", response?.data);

                if (response?.data?.status === true || response?.data?.status === 200 || response?.data?.success || response?.status === 200) {
                    toast.success(response?.data?.message || "Shift deleted successfully!");
                    setSchedule(prev => {
                        const updatedShifts = [...(prev[dayValue] || [])];
                        updatedShifts.splice(shiftIndex, 1);
                        return {
                            ...prev,
                            [dayValue]: updatedShifts
                        };
                    });
                    const whId = selectedTimingWarehouseId || effectiveWarehouseId || routeWarehouseId;
                    if (whId) {
                        fetchOperatingHours(whId);
                    }
                } else {
                    toast.error(response?.data?.message || "Failed to delete shift.");
                }
            } catch (error) {
                console.error("Delete Shift Error:", error);
                toast.error(error?.response?.data?.message || error?.message || "Failed to delete shift.");
            } finally {
                setDeletingShiftKey(null);
            }
        } else {
            // Newly added unsaved shift
            setSchedule(prev => {
                const updatedShifts = [...(prev[dayValue] || [])];
                updatedShifts.splice(shiftIndex, 1);
                return {
                    ...prev,
                    [dayValue]: updatedShifts
                };
            });
            toast.info("Shift removed");
        }
    };

    const applyDefaultTimings = () => {
        const defaultSchedule = {};
        const defaultClosed = {};
        days.forEach(d => {
            if (d.value === 7) {
                defaultClosed[d.value] = true;
                defaultSchedule[d.value] = [];
            } else {
                defaultClosed[d.value] = false;
                defaultSchedule[d.value] = [{ id: null, times: [dayjs("09:00", "HH:mm"), dayjs("18:00", "HH:mm")] }];
            }
        });
        setClosedDays(defaultClosed);
        setSchedule(defaultSchedule);
        toast.info("Applied 09:00 - 18:00 (Mon-Sat, Sun closed)");
    };

    // Fetch Operating Hours for warehouse
    const fetchOperatingHours = async (warehouseId) => {
        if (!warehouseId) {
            setSchedule(emptySchedule);
            setClosedDays(allClosed);
            setOperatingHoursExist(false);
            return;
        }

        try {
            setIsTimingLoading(true);
            const token = sessionStorage.getItem("token") || rawToken;
            const response = await axios.get(
                `${apiEndpoint}/snp/warehouse/operating-hours/get/${warehouseId}`,
                {
                    headers: {
                        "Content-Type": "application/json",
                        "x-custom-authorization": `Bearer ${token}`,
                    }
                }
            );

            const resData = response?.data?.data || response?.data;
            let hoursData = [];
            if (Array.isArray(resData)) {
                hoursData = resData;
            } else if (Array.isArray(resData?.operating_hours)) {
                hoursData = resData.operating_hours;
            } else if (Array.isArray(resData?.operating_days)) {
                hoursData = resData.operating_days;
            } else if (Array.isArray(resData?.operatingDays)) {
                hoursData = resData.operatingDays;
            } else if (Array.isArray(resData?.location?.[0]?.operating_days)) {
                hoursData = resData.location[0].operating_days;
            } else if (Array.isArray(resData?.location?.[0]?.operatingDays)) {
                hoursData = resData.location[0].operatingDays;
            } else if (Array.isArray(resData?.location?.operating_days)) {
                hoursData = resData.location.operating_days;
            }

            const activeStatus = resData?.status !== undefined
                ? Boolean(resData?.status)
                : (response?.data?.status !== undefined ? Boolean(response?.data?.status) : true);
            setIsOperatingHoursEnabled(activeStatus);

            populateOperatingSchedule(hoursData);
        } catch (error) {
            console.log("Get Operating Hours Error --->", error?.response?.data || error?.message);
            setOperatingHoursExist(false);
            setSchedule(emptySchedule);
            setClosedDays(allClosed);
        } finally {
            setIsTimingLoading(false);
        }
    };

    const handleToggleOperatingHours = async (newStatus) => {
        const whId = selectedTimingWarehouseId || effectiveWarehouseId;
        if (!whId) {
            toast.error("Please select or save a warehouse first!");
            return;
        }

        try {
            setIsTogglingStatus(true);
            const token = sessionStorage.getItem("token") || rawToken;
            const response = await axios({
                url: `${apiEndpoint}/snp/warehouse/operating-hours/enable-disable/${whId}`,
                method: "PUT",
                headers: {
                    "Content-Type": "application/json",
                    "x-custom-authorization": `Bearer ${token}`,
                },
                data: {
                    status: newStatus,
                },
            });

            if (response?.data?.status === true || response?.data?.status === 200 || response?.data?.success) {
                setIsOperatingHoursEnabled(newStatus);
                toast.success(response?.data?.message || `Operating hours ${newStatus ? "enabled" : "disabled"} successfully!`);
            } else {
                toast.error(response?.data?.message || "Failed to update status.");
            }
        } catch (error) {
            console.error("Enable/Disable Error:", error);
            toast.error(error?.response?.data?.message || error?.message || "Failed to update operating hours status.");
        } finally {
            setIsTogglingStatus(false);
        }
    };

    useEffect(() => {
        if (selectedTimingWarehouseId) {
            fetchOperatingHours(selectedTimingWarehouseId);
        }
    }, [selectedTimingWarehouseId]);

    // Save or Update Operating Hours
    const saveOperatingHours = async () => {
        const whId = selectedTimingWarehouseId || effectiveWarehouseId;
        if (!whId) {
            toast.error("Please select or save a warehouse first!");
            return;
        }

        try {
            setIsTimingLoading(true);
            const operating_hours = days.map(day => {
                const isClosed = Boolean(closedDays[day.value]);
                const rawDayShifts = schedule[day.value] || [];
                const validShifts = rawDayShifts.filter(s => {
                    const times = s?.times || (Array.isArray(s) ? s : null);
                    return times && times[0] && times[1];
                });

                if (operatingHoursExist) {
                    return {
                        day_of_week: day.value,
                        is_closed: isClosed || validShifts.length === 0,
                        status: true,
                        shifts: isClosed ? [] : validShifts.map(s => {
                            const times = s?.times || (Array.isArray(s) ? s : []);
                            return {
                                ...(s?.id ? { id: s.id } : {}),
                                opening_time: typeof times[0]?.format === "function" ? times[0].format("HH:mm") : String(times[0]).slice(0, 5),
                                closing_time: typeof times[1]?.format === "function" ? times[1].format("HH:mm") : String(times[1]).slice(0, 5),
                                status: true
                            };
                        })
                    };
                } else {
                    return {
                        day_of_week: day.value,
                        is_closed: isClosed || validShifts.length === 0,
                        shifts: isClosed ? [] : validShifts.map(s => {
                            const times = s?.times || (Array.isArray(s) ? s : []);
                            return {
                                ...(s?.id ? { id: s.id } : {}),
                                opening_time: typeof times[0]?.format === "function" ? times[0].format("HH:mm") : String(times[0]).slice(0, 5),
                                closing_time: typeof times[1]?.format === "function" ? times[1].format("HH:mm") : String(times[1]).slice(0, 5)
                            };
                        })
                    };
                }
            });

            const payload = {
                warehouse_id: Number(whId),
                operating_hours: operating_hours
            };

            const url = operatingHoursExist
                ? `${apiEndpoint}/snp/warehouse/operating-hours/update/${whId}`
                : `${apiEndpoint}/snp/warehouse/operating-hours/create`;

            const method = operatingHoursExist ? "PUT" : "POST";
            const token = sessionStorage.getItem("token") || rawToken;

            const response = await axios({
                url,
                method,
                headers: {
                    "Content-Type": "application/json",
                    "x-custom-authorization": `Bearer ${token}`,
                },
                data: payload
            });

            if (response?.data?.status === true || response?.data?.status === 200 || response?.data?.success) {
                toast.success(response?.data?.message || (operatingHoursExist ? "Operating hours updated successfully!" : "Operating hours created successfully!"));
                setOperatingHoursExist(true);
                if (whId) {
                    fetchOperatingHours(whId);
                }

                // Auto-switch to Fulfillment Tab (Tab 3) on status: true
                const targetWhId = whId || effectiveWarehouseId || routeWarehouseId || selectedTimingWarehouseId;
                const targetLocId = effectiveLocationId || routeLocationId || targetWhId;

                const whKey = targetWhId || "default";
                sessionStorage.setItem(`active_wh_tab_${whKey}`, "3");
                sessionStorage.setItem("active_warehouse_tab", "3");

                if (targetWhId) {
                    router.push(`/warehousefulfillment?id=${targetWhId}&warehouse_location_id=${targetLocId || targetWhId}&tab=3`);
                } else {
                    router.push(`/warehousefulfillment?tab=3`);
                }
            } else {
                toast.error(response?.data?.message || "Failed to save operating hours.");
            }
        } catch (error) {
            console.error("Save Operating Hours Error --->", error?.response?.data || error?.message);
            toast.error(error?.response?.data?.message || "Failed to save operating hours.");
        } finally {
            setIsTimingLoading(false);
        }
    };

    // Fetch seller bank details for the Bank Details tab.
    const fetchBankDetails = async (targetWhId) => {
        try {
            setIsBankLoading(true);
            const token = sessionStorage.getItem("token") || rawToken;
            const whId = targetWhId || effectiveWarehouseId || routeWarehouseId || selectedTimingWarehouseId;
            // Fetch warehouse-specific bank details ONLY
            if (whId) {
                try {
                    const resWh = await axios.get(
                        `${apiEndpoint}/snp/warehouse/get-bank-details/${whId}`,
                        {
                            headers: {
                                "Content-Type": "application/json",
                                "x-custom-authorization": `Bearer ${token}`,
                            }
                        }
                    );
                    const resWhData = resWh?.data?.data || resWh?.data?.bank_account || resWh?.data;
                    const bankObj = Array.isArray(resWhData) ? (resWhData[0] || null) : resWhData;

                    const hasValidBankData = bankObj && (
                        bankObj.account_no ||
                        bankObj.account_number ||
                        bankObj.accountnumber ||
                        bankObj.account_holder_name ||
                        bankObj.name ||
                        bankObj.ifsc_code ||
                        bankObj.ifsc
                    );

                    if (hasValidBankData) {
                        setBankDetails(bankObj);
                        return;
                    } else {
                        setBankDetails(null);
                        return;
                    }
                } catch (whErr) {
                    console.log("Warehouse specific bank details error:", whErr?.response?.data || whErr?.message);
                    setBankDetails(null);
                    return;
                }
            } else {
                setBankDetails(null);
            }
        } catch (err) {
            console.log("Get Bank Details Error:", err?.response?.data || err?.message);
            setBankDetails(null);
        } finally {
            setIsBankLoading(false);
        }
    };


    const fetchIfscLookup = async (ifscCode) => {
        const code = (ifscCode || "").trim().toUpperCase();
        if (code.length === 11 && /^[A-Z]{4}0[A-Z0-9]{6}$/.test(code)) {
            try {
                setModalIfscLoading(true);
                const res = await fetch(`https://ifsc.razorpay.com/${code}`);
                if (res.ok) {
                    const bankInfo = await res.json();
                    if (bankInfo?.BANK) {
                        setModalIfscInfo({
                            bank: bankInfo.BANK || "",
                            branch: bankInfo.BRANCH || ""
                        });
                        bankEditForm.setFieldsValue({
                            bank_name: bankInfo.BANK || "",
                            branch_name: bankInfo.BRANCH || ""
                        });
                    }
                } else {
                    setModalIfscInfo({ bank: "", branch: "" });
                }
            } catch (err) {
                console.log("Modal IFSC lookup error:", err);
            } finally {
                setModalIfscLoading(false);
            }
        } else {
            setModalIfscInfo({ bank: "", branch: "" });
        }
    };

    // Open Bank Edit Modal (for parent_id === 1)
    const openBankEditModal = () => {
        setIsBankVerifiedInModal(false);
        setVerifiedBankData(null);
        const initialIfsc = bankDetails?.ifsc_code || bankDetails?.ifsc || "";
        const initialBankName = bankDetails?.bank_name || bankDetails?.bankname || "";
        const initialBranchName = bankDetails?.branch_name || bankDetails?.branchname || "";

        setModalIfscInfo({
            bank: initialBankName,
            branch: initialBranchName
        });

        bankEditForm.setFieldsValue({
            account_holder_name: bankDetails?.account_holder_name || bankDetails?.name || "",
            bank_name: initialBankName,
            account_no: bankDetails?.account_no || bankDetails?.accountnumber || bankDetails?.account_number || "",
            ifsc_code: initialIfsc,
            branch_name: initialBranchName,
            account_type: bankDetails?.accounttype || bankDetails?.account_type || "savings",
        });

        if (initialIfsc && initialIfsc.length === 11 && /^[A-Z]{4}0[A-Z0-9]{6}$/.test(initialIfsc.toUpperCase()) && (!initialBankName || !initialBranchName)) {
            fetchIfscLookup(initialIfsc.toUpperCase());
        }

        setIsBankEditModalOpen(true);
    };

    // Step 1: Verify Bank Details via Meon API (POST /snp/verify/bank)
    const handleVerifyBank = async () => {
        try {
            await bankEditForm.validateFields(["account_holder_name", "account_no", "ifsc_code", "account_type"]);
            const values = bankEditForm.getFieldsValue();
            setIsVerifyingBank(true);
            const token = sessionStorage.getItem("token") || rawToken;
            const mobile = sessionStorage.getItem("mobile") || "";

            const meonPayload = {
                name: (values.account_holder_name || "").trim(),
                ifsc: (values.ifsc_code || "").trim().toUpperCase(),
                accountnumber: (values.account_no || "").trim(),
                // account_number: (values.account_no || "").trim(),
                accounttype: values.account_type || "savings",
                mobile: mobile
            };

            const meonRes = await axios.post(
                `${apiEndpoint}/snp/verify/bank`,
                meonPayload,
                {
                    headers: {
                        "Content-Type": "application/json",
                        "x-custom-authorization": `Bearer ${token}`
                    }
                }
            );

            const resObj = meonRes?.data?.response || meonRes?.data?.data || meonRes?.data || {};
            const msg = (meonRes?.data?.message || meonRes?.data?.error || "").toString().toLowerCase();
            const isAlreadyVerified = msg.includes("already verified") || msg.includes("already exist") || Boolean(resObj?.account_no || resObj?.accountnumber);
            const isSuccess = meonRes?.data?.status === true || meonRes?.data?.status === 200 || meonRes?.data?.success === true || meonRes?.status === 200 || isAlreadyVerified;

            if (!isSuccess) {
                toast.error(meonRes?.data?.message || meonRes?.data?.error || "Bank verification failed on Meon.");
                setIsBankVerifiedInModal(false);
                setVerifiedBankData(null);
                return;
            }

            // Success or Already Verified from verify/bank
            const verifiedInfo = {
                ...resObj,
                name: resObj.account_holder_name || resObj.name || resObj.beneficiary_name || values.account_holder_name,
                ifsc: (resObj.ifsc_code || resObj.ifsc || values.ifsc_code).toUpperCase(),
                accountnumber: resObj.account_no || resObj.accountnumber || resObj.account_number || values.account_no,
                account_number: resObj.account_no || resObj.accountnumber || resObj.account_number || values.account_no,
                accounttype: values.account_type || resObj.account_type || resObj.accounttype || "savings",
                account_type: values.account_type || resObj.account_type || resObj.accounttype || "savings",
                mobile: resObj.mobile || mobile,
                bank_name: resObj.bank_name || resObj.bankname || values.bank_name || "",
                branch_name: resObj.branch_name || resObj.branchname || values.branch_name || ""
            };

            setVerifiedBankData(verifiedInfo);
            setIsBankVerifiedInModal(true);
            toast.success(meonRes?.data?.message || "Bank details verified successfully! You can now update.");

            // Update form with verified details
            bankEditForm.setFieldsValue({
                account_holder_name: verifiedInfo.name,
                account_no: verifiedInfo.accountnumber,
                ifsc_code: verifiedInfo.ifsc,
                bank_name: verifiedInfo.bank_name,
                branch_name: verifiedInfo.branch_name,
                account_type: verifiedInfo.accounttype
            });

            if (verifiedInfo.bank_name || verifiedInfo.branch_name) {
                setModalIfscInfo({
                    bank: verifiedInfo.bank_name,
                    branch: verifiedInfo.branch_name
                });
            }
        } catch (err) {
            console.error("Verify Bank Error:", err);
            const errData = err?.response?.data;
            const errMsg = (errData?.message || errData?.error || err?.message || "").toString().toLowerCase();
            const resObj = errData?.response || errData?.data || {};
            const isAlreadyVerified = errMsg.includes("already verified") || errMsg.includes("already exist") || Boolean(resObj?.account_no || resObj?.accountnumber);

            if (isAlreadyVerified) {
                const values = bankEditForm.getFieldsValue();
                const mobile = sessionStorage.getItem("mobile") || "";
                const verifiedInfo = {
                    ...resObj,
                    name: resObj.account_holder_name || resObj.name || resObj.beneficiary_name || values.account_holder_name,
                    ifsc: (resObj.ifsc_code || resObj.ifsc || values.ifsc_code).toUpperCase(),
                    accountnumber: resObj.account_no || resObj.accountnumber || resObj.account_number || values.account_no,
                    account_number: resObj.account_no || resObj.accountnumber || resObj.account_number || values.account_no,
                    accounttype: values.account_type || resObj.account_type || resObj.accounttype || "savings",
                    account_type: values.account_type || resObj.account_type || resObj.accounttype || "savings",
                    mobile: resObj.mobile || mobile,
                    bank_name: resObj.bank_name || resObj.bankname || values.bank_name || "",
                    branch_name: resObj.branch_name || resObj.branchname || values.branch_name || ""
                };

                setVerifiedBankData(verifiedInfo);
                setIsBankVerifiedInModal(true);
                toast.success(errData?.message || "Bank details verified successfully! You can now update.");

                bankEditForm.setFieldsValue({
                    account_holder_name: verifiedInfo.name,
                    account_no: verifiedInfo.accountnumber,
                    ifsc_code: verifiedInfo.ifsc,
                    bank_name: verifiedInfo.bank_name,
                    branch_name: verifiedInfo.branch_name,
                    account_type: verifiedInfo.accounttype
                });

                if (verifiedInfo.bank_name || verifiedInfo.branch_name) {
                    setModalIfscInfo({
                        bank: verifiedInfo.bank_name,
                        branch: verifiedInfo.branch_name
                    });
                }
            } else {
                if (err?.errorFields) {
                    toast.error("Please fill all required fields correctly before verifying.");
                } else {
                    toast.error(errData?.message || errData?.error || err?.message || "Bank verification failed.");
                }
                setIsBankVerifiedInModal(false);
                setVerifiedBankData(null);
            }
        } finally {
            setIsVerifyingBank(false);
        }
    };

    // Step 2: Update Warehouse Bank Details (PUT /snp/warehouse/update-bank-details/:warehouse_id)
    const handleUpdateBankDetails = async () => {
        try {
            await bankEditForm.validateFields(["account_holder_name", "account_no", "ifsc_code", "account_type"]);
        } catch (err) {
            toast.error("Please fill all required fields correctly before updating.");
            return;
        }

        const token = sessionStorage.getItem("token") || rawToken;
        let decodedSellerId = 1;
        let userMobile = sessionStorage.getItem("mobile") || "";
        if (token) {
            try {
                const decoded = jwt_decode(token);
                const userObj = Array.isArray(decoded?.data?.user) ? decoded?.data?.user[0] : (decoded?.data?.user || {});
                decodedSellerId = userObj?.seller_id || decoded?.data?.seller_id || decoded?.data?.seller_data?.[0]?.id || 1;
                if (!userMobile) {
                    userMobile = userObj?.contact_no || decoded?.data?.seller_data?.[0]?.contact_no || "";
                }
            } catch (e) { }
        }

        const whId = effectiveWarehouseId || routeWarehouseId || selectedTimingWarehouseId || bankDetails?.warehouse_id || bankDetails?.id || sessionStorage.getItem("warehouse_id") || decodedSellerId;
        if (!whId) {
            toast.error("Warehouse ID not found. Please save or select the warehouse first.");
            return;
        }

        try {
            setIsBankUpdating(true);
            const formVals = bankEditForm.getFieldsValue();
            const updatePayload = {
                name: (formVals.account_holder_name || "").trim(),
                accountnumber: (formVals.account_no || "").trim(),
                accounttype: formVals.account_type || "savings",
                ifsc: (formVals.ifsc_code || "").trim().toUpperCase(),
                mobile: userMobile,
            };

            const res = await axios({
                url: `${apiEndpoint}/snp/warehouse/update-bank-details/${whId}`,
                method: "PUT",
                headers: {
                    "Content-Type": "application/json",
                    "x-custom-authorization": `Bearer ${token}`
                },
                data: updatePayload
            });
            console.log("Update Bank Details Result ->", res?.data);

            if (res?.data?.status === true || res?.data?.status === 200 || res?.status === 200 || res?.data?.success) {
                toast.success(res?.data?.message || "Warehouse bank details updated successfully!");
                setIsBankEditModalOpen(false);
                setIsBankVerifiedInModal(false);
                setVerifiedBankData(null);
                setBankDetails((prev) => ({
                    ...prev,
                    ...updatePayload,
                    name: updatePayload.name,
                    account_holder_name: updatePayload.name,
                    accountnumber: updatePayload.accountnumber,
                    account_no: updatePayload.accountnumber,
                    account_number: updatePayload.accountnumber,
                    accounttype: updatePayload.accounttype,
                    account_type: updatePayload.accounttype,
                    ifsc: updatePayload.ifsc,
                    ifsc_code: updatePayload.ifsc,
                    is_verified: true,
                    status: true
                }));
                if (whId) {
                    fetchBankDetails(whId);
                }
            } else {
                toast.error(res?.data?.message || "Failed to update bank details.");
            }
        } catch (err) {
            console.error("Update Bank Details Error:", err);
            toast.error(err?.response?.data?.message || err?.message || "Failed to update bank details.");
        } finally {
            setIsBankUpdating(false);
        }
    };

    // Fulfillment Columns
    const fulfillmentColumns = [
        {
            title: "Fulfillment Type",
            dataIndex: "fulfillment_type",
            key: "fulfillment_type",
            render: (text) => {
                let display = text;
                if (text === "ON-ORDER") display = "Prepaid";
                else if (text === "COD") display = "Cash On Delivery (COD)";
                return <Tag color="blue" style={{ fontWeight: 600 }}>{display || "—"}</Tag>;
            }
        },
        {
            title: "Method",
            dataIndex: "fulfillment_method",
            key: "fulfillment_method",
            render: (text) => <Tag color="purple" style={{ fontWeight: 600 }}>{text || "—"}</Tag>
        },
        {
            title: "Delivery Fee",
            dataIndex: "delivery_fee",
            key: "delivery_fee",
            render: (text) => <span style={{ fontWeight: 600, color: "#0F172A" }}>₹{Number(text || 0).toFixed(2)}</span>
        },
        {
            title: "Packing Charge",
            dataIndex: "packing_charge",
            key: "packing_charge",
            render: (text) => <span style={{ fontWeight: 600, color: "#0F172A" }}>₹{Number(text || 0).toFixed(2)}</span>
        },
        {
            title: "Miscellaneous Charge",
            dataIndex: "miscellaneous_charge",
            key: "miscellaneous_charge",
            render: (text) => <span style={{ fontWeight: 600, color: "#0F172A" }}>₹{Number(text || 0).toFixed(2)}</span>
        },
        {
            title: "Status",
            key: "status_action",
            align: "center",
            render: (_, record) => {
                const val = record?.is_active !== undefined ? record.is_active : record?.status;
                const isEnabled = val === true || val === 1 || val === "1" || val === "true";
                const isLoading = togglingFulfillmentId === record?.id;

                return (
                    <button
                        type="button"
                        disabled={isLoading}
                        className="btn btn-sm d-inline-flex align-items-center justify-content-center"
                        style={{
                            borderRadius: "20px",
                            border: isEnabled ? "1.5px solid #16A34A" : "1.5px solid #DC2626",
                            backgroundColor: isEnabled ? "#F0FDF4" : "#FEF2F2",
                            height: "34px",
                            padding: "0 14px",
                            gap: "8px",
                            boxShadow: "0 1px 2px rgba(0, 0, 0, 0.05)",
                            cursor: isLoading ? "not-allowed" : "pointer",
                            transition: "all 0.2s ease-in-out"
                        }}
                        onClick={() => handleToggleFulfillmentStatus(record?.id, isEnabled)}
                        title={isEnabled ? "Click to Disable" : "Click to Enable"}
                    >
                        {isLoading ? (
                            <Spin size="small" />
                        ) : isEnabled ? (
                            <FaToggleOn size={24} color="#16A34A" />
                        ) : (
                            <FaToggleOff size={24} color="#DC2626" />
                        )}
                        <span style={{ fontSize: "13px", fontWeight: 700, color: isEnabled ? "#16A34A" : "#DC2626" }}>
                            {isEnabled ? "Enabled" : "Disabled"}
                        </span>
                    </button>
                );
            }
        },
        {
            title: "Edit",
            key: "edit_action",
            align: "center",
            render: (_, record) => (
                <button
                    type="button"
                    className="btn btn-sm btn-outline-primary d-inline-flex align-items-center justify-content-center"
                    style={{ borderRadius: "8px", width: "32px", height: "32px", padding: 0 }}
                    onClick={() => fetchFulfillmentById(record?.id)}
                    title="Edit Fulfillment"
                >
                    <HiPencilSquare size={16} />
                </button>
            ),
        },
        // {
        //     title: "Delete",
        //     key: "delete_action",
        //     align: "center",
        //     render: (_, record) => (
        //         <button
        //             type="button"
        //             className="btn btn-sm btn-outline-danger d-inline-flex align-items-center justify-content-center"
        //             style={{ borderRadius: "8px", width: "32px", height: "32px", padding: 0 }}
        //             onClick={() => deleteFulfillment(record?.id)}
        //             title="Delete Fulfillment"
        //         >
        //             <FaTrash size={14} />
        //         </button>
        //     ),
        // },
    ];

    // Serviceability Columns
    const serviceabilityColumns = [
        {
            title: "Provider ID",
            dataIndex: "provider_id",
            key: "provider_id",
            render: (text) => <span style={{ fontWeight: 600, color: "#475569" }}>{text || "—"}</span>
        },
        {
            title: "Service City",
            dataIndex: "served_city_list",
            key: "served_city_list",
            render: (_, record) => {
                const cities = (record?.served_city_list && record.served_city_list.length > 0)
                    ? record.served_city_list
                    : record?.service_city;

                if (Array.isArray(cities)) {
                    return cities.length > 0 ? (
                        <div style={{ display: "flex", flexWrap: "wrap", gap: "4px", maxWidth: "280px" }}>
                            {cities.map((c, i) => (
                                <Tag color="geekblue" key={i} style={{ borderRadius: "4px", fontSize: "12px", fontWeight: 500 }}>
                                    {c}
                                </Tag>
                            ))}
                        </div>
                    ) : "—";
                }
                return <Tag color="geekblue" style={{ borderRadius: "4px", fontSize: "12px", fontWeight: 500 }}>{cities || "—"}</Tag>;
            }
        },
        {
            title: "Service Range (km)",
            dataIndex: "service_range",
            key: "service_range",
            render: (text) => <span>{text ? `${text} km` : "—"}</span>
        },
        {
            title: "Service Type",
            dataIndex: "service_type",
            key: "service_type",
            render: (text) => <Tag color="green" style={{ fontWeight: 600, textTransform: "capitalize" }}>{text || "—"}</Tag>
        },
        {
            title: "Warehouse Loc ID",
            dataIndex: "warehouse_location_id",
            key: "warehouse_location_id",
            render: (text) => <span style={{ color: "#64748B" }}>{text || "—"}</span>
        },

        {
            title: "Edit",
            key: "edit_action",
            align: "center",
            render: (_, record) => (
                <button
                    type="button"
                    className="btn btn-sm btn-outline-primary d-inline-flex align-items-center justify-content-center"
                    style={{ borderRadius: "8px", width: "32px", height: "32px", padding: 0 }}
                    onClick={() => {
                        setUserChangedHyperCity(false);
                        setServiceabilityEditId(record?.id || null);
                        setServiceFeature(record?.feature || "");

                        const servedList = Array.isArray(record?.served_city_list)
                            ? record.served_city_list
                            : (record?.served_city_list ? [record.served_city_list] : []);

                        const hyperCity = servedList.length > 0
                            ? servedList[0]
                            : (Array.isArray(record?.service_city) ? (record.service_city[0] || "") : (record?.service_city || ""));

                        setServiceData({
                            service_type: record?.service_type || "hyperlocal",
                            service_range: record?.service_range || "",
                            service_city: hyperCity,
                            served_city_list: servedList   // ✅ ab poori list preserve hogi (4 cities)
                        });

                        if (record?.service_type === "intercity") {
                            const interCities = servedList.length > 0 ? servedList : record?.service_city;
                            if (Array.isArray(interCities)) {
                                setSelectedServiceCities(interCities.map(s => String(s).trim()));
                            } else if (interCities) {
                                setSelectedServiceCities(String(interCities).split(",").map(s => s.trim()));
                            } else {
                                setSelectedServiceCities([]);
                            }
                        } else {
                            setSelectedServiceCities([]);
                        }
                        setShowServiceabilityForm(true);
                    }}
                    title="Edit Serviceability"
                >
                    <HiPencilSquare size={16} />
                </button>
            )
        },
        // {
        //     title: "Delete",
        //     key: "delete_action",
        //     align: "center",
        //     render: (_, record) => (
        //         <button
        //             type="button"
        //             className="btn btn-sm btn-outline-danger d-inline-flex align-items-center justify-content-center"
        //             style={{ borderRadius: "8px", width: "32px", height: "32px", padding: 0 }}
        //             onClick={() => deleteServiceability(record?.id)}
        //             title="Delete Serviceability"
        //         >
        //             <FaTrash size={14} />
        //         </button>
        //     )
        // }
    ];

    const tabItems = [
        {
            key: '1',
            label: (
                <span className="d-flex align-items-center" style={{ gap: "8px", fontWeight: 600, fontSize: "14px" }}>
                    <FaWarehouse size={16} />
                    {effectiveWarehouseId ? "Edit Warehouse" : "Add Warehouse"}
                    {addressSaved && <FaCheckCircle color="#16A34A" size={14} />}
                </span>
            ),
            children: (
                <div style={{ backgroundColor: "#FFFFFF", padding: "24px", borderRadius: "10px", border: "1px solid #E2E8F0" }}>
                    <div className="mb-4 pb-2" style={{ borderBottom: "1px solid #F1F5F9" }}>
                        <h5 style={{ fontWeight: 700, margin: 0, color: "#0F172A" }}>
                            {effectiveWarehouseId ? "Edit Warehouse Details" : "Warehouse Basic Details"}
                        </h5>
                        <p style={{ fontSize: "13px", color: "#64748B", margin: 0 }}>
                            {effectiveWarehouseId
                                ? "Update the location, contact and registration information for this warehouse."
                                : "Fill in the details below to register a new warehouse in your store."}
                        </p>
                    </div>

                    <Form
                        {...formItemLayout}
                        form={form}
                        name="warehouse_info_form"
                        onFinish={onFinish}
                        onValuesChange={(changedValues, allValues) => {
                            if (changedValues && "area_code" in changedValues) {
                                const val = (changedValues.area_code || "").toString().replace(/\D/g, "");
                                if (!val || val.length < 6) {
                                    form.setFieldsValue({ state: undefined, city: undefined });
                                    setSelectedState("");
                                    setCities([]);
                                    setPincodeStatus(null);
                                    setIsPincodeLoading(false);
                                }
                            }
                            if (!effectiveWarehouseId) {
                                const draftData = {
                                    ...allValues,
                                    citiesList: cities
                                };
                                sessionStorage.setItem("add_warehouse_draft", JSON.stringify(draftData));
                                localStorage.setItem("add_warehouse_draft", JSON.stringify(draftData));
                            }
                        }}
                        scrollToFirstError
                    >
                        <Form.Item
                            name="warehouse_name"
                            label="Warehouse Name"
                            rules={[
                                { required: true, message: 'Please enter warehouse name!' },
                                {
                                    pattern: /^[a-zA-Z0-9\s.,&'-]+$/,
                                    message: 'Special characters (like @, #, $, %, etc.) are not allowed in warehouse name!'
                                },
                                {
                                    validator: (_, value) => {
                                        if (value && /\s{2,}/.test(value)) {
                                            return Promise.reject(new Error('More than 1 consecutive space is not allowed!'));
                                        }
                                        if (value && value.trim().length < 3) {
                                            return Promise.reject(new Error('Warehouse name must be at least 3 characters long!'));
                                        }
                                        return Promise.resolve();
                                    }
                                }
                            ]}
                            normalize={(value) =>
                                (value || "")
                                    .replace(/^\s+/, "")
                                    .replace(/\s{2,}/g, " ")
                            }
                        >
                            <Input
                                placeholder="e.g. Central Hub Delhi"
                                size="large"
                                style={{ borderRadius: "6px" }}
                                onKeyPress={(e) => {
                                    if (!/[a-zA-Z0-9\s.,&'-]/.test(e.key)) {
                                        e.preventDefault();
                                    }
                                }}
                            />
                        </Form.Item>

                        <Form.Item
                            name="warehouse_code"
                            label="Warehouse Code"
                            rules={[
                                {
                                    validator: (_, value) => {
                                        if (value && /\s/.test(value)) {
                                            return Promise.reject(new Error('Spaces are not allowed in warehouse code!'));
                                        }
                                        return Promise.resolve();
                                    }
                                }
                            ]}
                            normalize={(value) => (value || "").replace(/\s/g, "").toUpperCase()}
                        >
                            <Input placeholder="e.g. WH-DEL-01" size="large" style={{ borderRadius: "6px", textTransform: "uppercase" }} />
                        </Form.Item>

                        <Form.Item
                            name="warehouse_contact_no"
                            label="Contact Number"
                            rules={[
                                { required: true, message: 'Please enter contact number!' },
                                {
                                    validator: (_, value) => {
                                        if (!value) {
                                            return Promise.resolve();
                                        }
                                        const trimmed = String(value).trim();
                                        if (trimmed.startsWith('0')) {
                                            return Promise.reject(new Error('Contact number cannot start with 0!'));
                                        }
                                        if (!/^[6-9][0-9]{9}$/.test(trimmed)) {
                                            return Promise.reject(new Error('Enter a valid 10-digit mobile number starting with 6-9!'));
                                        }
                                        return Promise.resolve();
                                    }
                                }
                            ]}
                            normalize={(value) => (value || "").toString().replace(/\D/g, "").slice(0, 10)}
                        >
                            <Input
                                placeholder="10-digit mobile number (e.g. 9876543210)"
                                size="large"
                                style={{ borderRadius: "6px" }}
                                maxLength={10}
                                onKeyPress={(e) => {
                                    if (!/[0-9]/.test(e.key)) e.preventDefault();
                                }}
                            />
                        </Form.Item>

                        <Form.Item
                            name="email"
                            label="Email Address"
                            rules={[
                                { required: true, message: 'Please enter email address!' },
                                { type: 'email', message: 'Please enter valid email!' },
                                {
                                    validator: (_, value) => {
                                        if (value && /\s/.test(value)) {
                                            return Promise.reject(new Error('Spaces are not allowed in email address!'));
                                        }
                                        return Promise.resolve();
                                    }
                                }
                            ]}
                            normalize={(value) => (value || "").replace(/\s/g, "").trim()}
                        >
                            <Input placeholder="warehouse@example.com" size="large" style={{ borderRadius: "6px" }} />
                        </Form.Item>

                        <Form.Item
                            name="fssai_license_no"
                            label="FSSAI License No."
                            rules={[
                                { pattern: /^[0-9]{1,14}$/, message: 'FSSAI number must be digits only (max 14)!' }
                            ]}
                            normalize={(value) => (value || "").toString().replace(/\D/g, "").slice(0, 14)}
                        >
                            <Input
                                placeholder="14-digit FSSAI number"
                                size="large"
                                style={{ borderRadius: "6px" }}
                                maxLength={14}
                                onKeyPress={(e) => {
                                    if (!/[0-9]/.test(e.key)) e.preventDefault();
                                }}
                            />
                        </Form.Item>

                        <Form.Item
                            name="street"
                            label="Street / Address"
                            rules={[
                                { required: true, message: 'Please enter street address!' },
                                {
                                    validator: (_, value) => {
                                        if (!value) return Promise.resolve();

                                        // No leading or trailing spaces
                                        if (/^\s|\s$/.test(value)) {
                                            return Promise.reject(new Error('Address cannot start or end with a space!'));
                                        }

                                        // No more than 1 consecutive space
                                        if (/\s{2,}/.test(value)) {
                                            return Promise.reject(new Error('More than 1 consecutive space is not allowed in address!'));
                                        }

                                        // Minimum length
                                        if (value.trim().length < 5) {
                                            return Promise.reject(new Error('Address must be at least 5 characters long!'));
                                        }

                                        // Maximum length
                                        if (value.trim().length > 100) {
                                            return Promise.reject(new Error('Address cannot exceed 100 characters!'));
                                        }

                                        // Allowed characters: letters, numbers, spaces, comma, period, hyphen, #, /, apostrophe
                                        if (!/^[a-zA-Z0-9\s,.\-#/']+$/.test(value)) {
                                            return Promise.reject(new Error('Address contains invalid characters! Only letters, numbers, spaces, and , . - # / \' are allowed.'));
                                        }

                                        // Must not be only numbers
                                        if (/^[0-9\s]+$/.test(value)) {
                                            return Promise.reject(new Error('Address cannot contain only numbers!'));
                                        }

                                        // Must not be only special characters/punctuation
                                        if (/^[,.\-#/'\s]+$/.test(value)) {
                                            return Promise.reject(new Error('Address cannot contain only special characters!'));
                                        }

                                        return Promise.resolve();
                                    }
                                }
                            ]}
                            normalize={(value) =>
                                (value || "")
                                    .replace(/^\s+/, "")
                                    .replace(/\s{2,}/g, " ")
                            }
                        >
                            <Input placeholder="Building name, street, road" size="large" style={{ borderRadius: "6px" }} />
                        </Form.Item>

                        <Form.Item
                            name="locality"
                            label="Locality"
                            rules={[
                                { required: true, message: 'Please enter locality!' },
                                {
                                    validator: (_, value) => {
                                        if (value && /\s{2,}/.test(value)) {
                                            return Promise.reject(new Error('More than 1 consecutive space is not allowed in locality!'));
                                        }
                                        return Promise.resolve();
                                    }
                                }
                            ]}
                            normalize={(value) =>
                                (value || "")
                                    .replace(/^\s+/, "")
                                    .replace(/\s{2,}/g, " ")
                            }
                        >
                            <Input placeholder="Locality / Landmark" size="large" style={{ borderRadius: "6px" }} />
                        </Form.Item>

                        <Form.Item
                            name="area_code"
                            label="Area Pincode"
                            rules={[
                                { required: true, message: 'Please enter area pincode!' },
                                { pattern: /^[0-9]{6}$/, message: 'Pincode must be exactly 6 digits!' }
                            ]}
                            normalize={(value) => (value || "").toString().replace(/\D/g, "").slice(0, 6)}
                        >
                            <Input
                                placeholder="6-digit pincode"
                                size="large"
                                style={{ borderRadius: "6px" }}
                                onChange={handlePincodeChange}
                                maxLength={6}
                                onKeyPress={(e) => {
                                    if (!/[0-9]/.test(e.key)) e.preventDefault();
                                }}
                                suffix={
                                    <span style={{ display: "inline-flex", alignItems: "center", pointerEvents: "none" }}>
                                        {isPincodeLoading || pincodeStatus === "loading" ? (
                                            <span className="d-flex align-items-center gap-1" style={{ fontSize: "12px", color: "#64748B", fontWeight: 500 }}>
                                                <Spin size="small" />
                                                <span style={{ marginLeft: "4px" }}>Verifying...</span>
                                            </span>
                                        ) : pincodeStatus === "success" ? (
                                            <span className="d-flex align-items-center" title="Valid Pincode">
                                                <FaCheckCircle color="#16A34A" size={16} />
                                            </span>
                                        ) : pincodeStatus === "error" ? (
                                            <span className="d-flex align-items-center" title="Invalid Pincode">
                                                <FaTimesCircle color="#DC2626" size={16} />
                                            </span>
                                        ) : null}
                                    </span>
                                }
                            />
                        </Form.Item>

                        <Form.Item
                            name="state"
                            label="State"
                            rules={[{ required: true, message: 'Please enter a valid pincode to fetch state!' }]}
                        >
                            <Input
                                placeholder={isPincodeLoading ? "Fetching state..." : "Auto-filled from pincode"}
                                size="large"
                                disabled
                                style={{ borderRadius: "6px", backgroundColor: "#F8FAFC", color: "#0F172A", cursor: "not-allowed" }}
                            />
                        </Form.Item>

                        <Form.Item
                            name="city"
                            label="City"
                            rules={[{ required: true, message: 'Please enter a valid pincode to fetch city!' }]}
                        >
                            <Input
                                placeholder={isPincodeLoading ? "Fetching city..." : "Auto-filled from pincode"}
                                size="large"
                                disabled
                                style={{ borderRadius: "6px", backgroundColor: "#F8FAFC", color: "#0F172A", cursor: "not-allowed" }}
                            />
                        </Form.Item>

                        <Form.Item
                            name="status"
                            label="Active Status"
                            valuePropName="checked"
                            initialValue={true}
                        >
                            <Switch checkedChildren="Active" unCheckedChildren="Inactive" />
                        </Form.Item>

                        <Form.Item {...tailFormItemLayout}>
                            <div className="d-flex" style={{ gap: "12px" }}>
                                <Button
                                    type="primary"
                                    htmlType="submit"
                                    size="large"
                                    loading={isLoading}
                                    style={{ borderRadius: "6px", minWidth: "160px", fontWeight: 600 }}
                                >
                                    {isLoading ? "Saving..." : (effectiveWarehouseId ? "Update Warehouse" : "Save & Continue")}
                                </Button>
                            </div>
                        </Form.Item>
                    </Form>
                </div>
            ),
        },
        {
            key: '2',
            label: (
                <span className="d-flex align-items-center" style={{ gap: "8px", fontWeight: 600, fontSize: "14px" }}>
                    <FaClock size={16} />
                    Operating Hours
                    {operatingHoursExist && <FaCheckCircle color="#16A34A" size={14} />}
                </span>
            ),
            children: (
                <div style={{ backgroundColor: "#FFFFFF", padding: "24px", borderRadius: "10px", border: "1px solid #E2E8F0" }}>
                    {/* Operating Hours Bar */}
                    <div className="p-3 mb-4 d-flex justify-content-between align-items-center flex-wrap" style={{ backgroundColor: "#F8FAFC", borderRadius: "8px", border: "1px solid #E2E8F0", gap: "16px" }}>
                        <div className="d-flex align-items-center flex-wrap" style={{ gap: "12px" }}>
                            {/* Enable / Disable Switch */}
                            <div
                                className="d-flex align-items-center px-3 py-1"
                                style={{
                                    backgroundColor: isOperatingHoursEnabled ? "#F0FDF4" : "#FEF2F2",
                                    border: `1px solid ${isOperatingHoursEnabled ? "#BBF7D0" : "#FECACA"}`,
                                    borderRadius: "6px",
                                    gap: "8px"
                                }}
                            >
                                <span style={{ fontSize: "12px", fontWeight: 700, color: isOperatingHoursEnabled ? "#166534" : "#991B1B" }}>
                                    {isOperatingHoursEnabled ? "Operating Hours: Active" : "Operating Hours: Disabled"}
                                </span>
                                <Switch
                                    size="small"
                                    checked={isOperatingHoursEnabled}
                                    loading={isTogglingStatus}
                                    onChange={(checked) => handleToggleOperatingHours(checked)}
                                    style={{ backgroundColor: isOperatingHoursEnabled ? "#16A34A" : "#94A3B8" }}
                                />
                            </div>
                        </div>

                        <Button
                            size="small"
                            type="default"
                            disabled={!isOperatingHoursEnabled}
                            onClick={applyDefaultTimings}
                            style={{ borderRadius: "6px", fontSize: "12px", fontWeight: 600 }}
                        >
                            Apply Default (09:00 - 18:00, Mon-Sat)
                        </Button>
                    </div>

                    {!effectiveWarehouseId && !selectedTimingWarehouseId && (
                        <div
                            className="alert alert-info d-flex align-items-center mb-3 py-2 px-3"
                            style={{ borderRadius: "8px", fontSize: "13px", backgroundColor: "#EFF6FF", border: "1px solid #BFDBFE", color: "#1E40AF" }}
                        >
                            <FaInfoCircle size={16} className="mr-2" />
                            <span>
                                You are creating a new warehouse. Save <strong>Warehouse Details (Tab 1)</strong> first, or select an existing warehouse above to configure operating hours.
                            </span>
                        </div>
                    )}

                    {!isOperatingHoursEnabled && (
                        <div
                            className="alert alert-danger d-flex align-items-center mb-3 py-2 px-3"
                            style={{ borderRadius: "8px", fontSize: "13px", backgroundColor: "#FEF2F2", border: "1px solid #FECACA", color: "#991B1B" }}
                        >
                            <span style={{ fontWeight: 600 }}>
                                ⚠️ Operating hours are currently disabled for this warehouse. Toggle the switch above to enable.
                            </span>
                        </div>
                    )}

                    <div className="mb-3" style={{ opacity: isOperatingHoursEnabled ? 1 : 0.6 }}>
                        <h6 style={{ fontWeight: 700, margin: 0, color: "#0F172A" }}>
                            Weekly Operating Schedule
                        </h6>
                        <p style={{ fontSize: "12px", color: "#64748B", margin: 0 }}>
                            Configure shifts and open/closed status for each day of the week.
                        </p>
                    </div>

                    {/* Schedule Grid with visual feeka/faded effect when disabled */}
                    <div
                        style={{
                            display: 'grid',
                            gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))',
                            gap: '16px',
                            opacity: isOperatingHoursEnabled ? 1 : 0.45,
                            filter: isOperatingHoursEnabled ? 'none' : 'grayscale(0.7)',
                            pointerEvents: isOperatingHoursEnabled ? 'auto' : 'none',
                            transition: 'all 0.3s ease',
                            userSelect: isOperatingHoursEnabled ? 'auto' : 'none'
                        }}
                    >
                        {days.map(day => {
                            const isClosed = Boolean(closedDays[day.value]);
                            const dayShifts = schedule[day.value] || [];

                            return (
                                <div
                                    key={day.value}
                                    style={{
                                        border: isClosed ? '1px solid #E2E8F0' : '1.5px solid #BAE6FD',
                                        backgroundColor: isClosed ? '#F8FAFC' : '#FFFFFF',
                                        padding: "14px",
                                        borderRadius: "10px",
                                        boxShadow: isClosed ? "none" : "0 2px 8px rgba(14, 165, 233, 0.08)",
                                        transition: "all 0.2s ease"
                                    }}
                                >
                                    {/* Card Header */}
                                    <div className="d-flex justify-content-between align-items-center mb-2 pb-2" style={{ borderBottom: "1px solid #F1F5F9" }}>
                                        <strong style={{ fontSize: "14px", color: isClosed ? "#64748B" : "#0F172A" }}>
                                            {day.label}
                                        </strong>
                                        <div className="d-flex align-items-center" style={{ gap: "8px" }}>
                                            <Tag color={isClosed ? "red" : "green"} style={{ margin: 0, fontWeight: 600 }}>
                                                {isClosed ? "Closed" : "Open"}
                                            </Tag>
                                            <Switch
                                                size="small"
                                                checked={!isClosed}
                                                disabled={!isOperatingHoursEnabled}
                                                onChange={(checked) => handleDayToggle(day.value, checked)}
                                                style={{ backgroundColor: !isClosed ? "#16A34A" : "#94A3B8" }}
                                            />
                                        </div>
                                    </div>

                                    {/* Card Body */}
                                    {!isClosed ? (
                                        <div>
                                            {dayShifts.map((shift, sIndex) => {
                                                const shiftTimes = shift?.times || (Array.isArray(shift) ? shift : null);
                                                const shiftId = shift?.id;
                                                const isThisDeleting = deletingShiftKey === `${day.value}-${sIndex}-${shiftId}`;
                                                return (
                                                    <div key={sIndex} className="d-flex align-items-center justify-content-between gap-2 mt-2">
                                                        <TimePicker.RangePicker
                                                            format="HH:mm"
                                                            value={shiftTimes?.length ? shiftTimes : null}
                                                            disabled={!isOperatingHoursEnabled || Boolean(deletingShiftKey)}
                                                            onChange={(time) => handleTimeChange(day.value, sIndex, time)}
                                                            style={{ width: "100%", borderRadius: "6px" }}
                                                        />
                                                        {dayShifts.length > 1 && (
                                                            <Popconfirm
                                                                title="Delete Shift"
                                                                description="Are you sure you want to delete this shift?"
                                                                onConfirm={() => removeShift(day.value, sIndex)}
                                                                okText="Yes, Delete"
                                                                cancelText="No"
                                                                okButtonProps={{ danger: true, loading: isThisDeleting }}
                                                                disabled={!isOperatingHoursEnabled || Boolean(deletingShiftKey)}
                                                            >
                                                                <button
                                                                    type="button"
                                                                    className="btn btn-sm btn-link text-danger p-0 ml-1"
                                                                    disabled={!isOperatingHoursEnabled || isThisDeleting}
                                                                    title="Delete Shift"
                                                                    style={{ cursor: isOperatingHoursEnabled && !isThisDeleting ? "pointer" : "not-allowed", minWidth: "20px", textAlign: "center" }}
                                                                >
                                                                    {isThisDeleting ? (
                                                                        <Spin size="small" />
                                                                    ) : (
                                                                        <i className="fas fa-trash-alt" style={{ fontSize: "13px" }}></i>
                                                                    )}
                                                                </button>
                                                            </Popconfirm>
                                                        )}
                                                    </div>
                                                );
                                            })}

                                            <div className="mt-2 text-right">
                                                <Tag
                                                    color="blue"
                                                    onClick={() => isOperatingHoursEnabled && addShift(day.value)}
                                                    style={{
                                                        cursor: isOperatingHoursEnabled ? "pointer" : "not-allowed",
                                                        borderRadius: "4px",
                                                        padding: "2px 8px",
                                                        fontSize: "11px",
                                                        fontWeight: 600
                                                    }}
                                                >
                                                    + Add Shift
                                                </Tag>
                                            </div>
                                        </div>
                                    ) : (
                                        <div className="py-3 text-center text-muted" style={{ fontSize: "12px", fontStyle: "italic" }}>
                                            Warehouse is closed on {day.label}
                                        </div>
                                    )}
                                </div>
                            );
                        })}
                    </div>

                    <div className='d-flex justify-content-center mt-4'>
                        <button
                            className="btn btn-primary px-4 py-2"
                            style={{ borderRadius: "20px", minWidth: "220px", fontWeight: 600 }}
                            type="button"
                            disabled={isTimingLoading || !isOperatingHoursEnabled}
                            onClick={saveOperatingHours}
                        >
                            {isTimingLoading ? "Saving..." : (operatingHoursExist ? "Update Operating Hours" : "Save Operating Hours")}
                        </button>
                    </div>
                </div>
            ),
        },
        {
            key: '3',
            label: (
                <span className="d-flex align-items-center" style={{ gap: "8px", fontWeight: 600, fontSize: "14px" }}>
                    <FaTruck size={16} />
                    Fulfillments
                    {fulfillmentList.length > 0 && <Tag color="blue" style={{ marginLeft: 4, borderRadius: 10, padding: "0 6px", fontSize: 11 }}>{fulfillmentList.length}</Tag>}
                </span>
            ),
            children: (
                <div style={{ backgroundColor: "#FFFFFF", padding: "24px", borderRadius: "10px", border: "1px solid #E2E8F0" }}>
                    <div className="d-flex justify-content-between align-items-center mb-4 pb-2 flex-wrap" style={{ borderBottom: "1px solid #F1F5F9", gap: "12px" }}>
                        <div>
                            <h5 style={{ fontWeight: 700, margin: 0, color: "#0F172A" }}>
                                Warehouse Fulfillments
                            </h5>
                            <p style={{ fontSize: "13px", color: "#64748B", margin: 0 }}>
                                Configure payment modes, fulfillment types, and shipping charges.
                            </p>
                        </div>
                        <div className="d-flex align-items-center" style={{ gap: "8px" }}>
                            {!showFulfillmentForm ? (
                                <Button
                                    type="primary"
                                    icon={<FaPlus size={12} style={{ marginRight: 6 }} />}
                                    onClick={() => {
                                        setFulfillmentForm(initialFulfillmentData);
                                        setFulfillmentEditId(null);
                                        setShowFulfillmentForm(true);
                                    }}
                                    style={{ borderRadius: "6px", fontWeight: 600 }}
                                >
                                    Add Fulfillment
                                </Button>
                            ) : (
                                <Button
                                    onClick={() => {
                                        setShowFulfillmentForm(false);
                                        setFulfillmentEditId(null);
                                    }}
                                    style={{ borderRadius: "6px", fontWeight: 500 }}
                                >
                                    ← View All Fulfillments
                                </Button>
                            )}

                        </div>
                    </div>

                    {!showFulfillmentForm ? (
                        <div>
                            <Table
                                columns={fulfillmentColumns}
                                dataSource={fulfillmentList}
                                rowKey="id"
                                loading={isFulfillmentLoading}
                                pagination={false}

                                scroll={{ x: true }}
                            />
                        </div>
                    ) : (
                        <div className="p-3" style={{ backgroundColor: "#F8FAFC", borderRadius: "10px", border: "1px solid #E2E8F0" }}>
                            <h6 style={{ fontWeight: 700, color: "#0F172A", marginBottom: "16px" }}>
                                {fulfillmentEditId ? "Edit Fulfillment Configuration" : "New Fulfillment Configuration"}
                            </h6>
                            <div className="row">
                                <div className="col-md-6 mb-3">
                                    <label className="form-label" style={{ fontWeight: 600, fontSize: "13px" }}>
                                        Fulfillment Type <span className="text-danger">*</span>
                                    </label>
                                    <Select
                                        style={{ width: "100%" }}
                                        size="large"
                                        placeholder="Select type"
                                        value={fulfillmentForm.fulfillment_type || undefined}
                                        status={fulfillmentErrors.fulfillment_type ? "error" : ""}
                                        onChange={(val) => {
                                            setFulfillmentForm(prev => ({ ...prev, fulfillment_type: val }));
                                            setFulfillmentErrors(prev => {
                                                const updated = { ...prev, fulfillment_type: undefined };
                                                if (!fulfillmentForm.fulfillment_method) {
                                                    updated.fulfillment_method = "Please select Fulfillment Method!";
                                                }
                                                return updated;
                                            });
                                        }}
                                        options={[
                                            { label: "Prepaid", value: "ON-ORDER" },
                                            // { label: "Cash On Delivery (COD)", value: "COD" }
                                        ]}
                                    />
                                    {fulfillmentErrors.fulfillment_type && (
                                        <span className="text-danger" style={{ fontSize: "12px", display: "block", marginTop: "4px", fontWeight: 500 }}>
                                            {fulfillmentErrors.fulfillment_type}
                                        </span>
                                    )}
                                </div>

                                <div className="col-md-6 mb-3">
                                    <label className="form-label" style={{ fontWeight: 600, fontSize: "13px" }}>
                                        Fulfillment Method <span className="text-danger">*</span>
                                    </label>
                                    <Select
                                        style={{ width: "100%" }}
                                        size="large"
                                        placeholder="Select method"
                                        value={fulfillmentForm.fulfillment_method || undefined}
                                        status={fulfillmentErrors.fulfillment_method ? "error" : ""}
                                        onChange={(val) => {
                                            setFulfillmentForm(prev => ({ ...prev, fulfillment_method: val }));
                                            setFulfillmentErrors(prev => {
                                                const updated = { ...prev, fulfillment_method: undefined };
                                                if (!fulfillmentForm.fulfillment_type) {
                                                    updated.fulfillment_type = "Please select Fulfillment Type!";
                                                }
                                                return updated;
                                            });
                                        }}
                                        options={[
                                            { label: "Delivery", value: "Delivery" },
                                            { label: "Self Pickup", value: "Self-Pickup" }
                                        ]}
                                    />
                                    {fulfillmentErrors.fulfillment_method && (
                                        <span className="text-danger" style={{ fontSize: "12px", display: "block", marginTop: "4px", fontWeight: 500 }}>
                                            {fulfillmentErrors.fulfillment_method}
                                        </span>
                                    )}
                                </div>

                                <div className="col-md-4 mb-3">
                                    <label className="form-label" style={{ fontWeight: 600, fontSize: "13px" }}>
                                        Delivery Fee (₹)
                                    </label>
                                    <Input
                                        type="text"
                                        min="0"
                                        step="any"
                                        maxLength="7"
                                        size="large"
                                        placeholder="0.00"
                                        value={fulfillmentForm.delivery_fee}
                                        status={fulfillmentErrors.delivery_fee ? "error" : ""}
                                        onChange={(e) => {
                                            let val = e.target.value;
                                            // Allow only digits and one dot, max 4 digits before dot and 2 after
                                            val = val.replace(/[^0-9.]/g, "");
                                            const parts = val.split(".");
                                            if (parts.length > 2) val = parts[0] + "." + parts.slice(1).join("");
                                            if (parts[0] && parts[0].length > 4) parts[0] = parts[0].slice(0, 4);
                                            if (parts[1] && parts[1].length > 2) parts[1] = parts[1].slice(0, 2);
                                            val = parts.length > 1 ? parts[0] + "." + parts[1] : parts[0];
                                            setFulfillmentForm({ ...fulfillmentForm, delivery_fee: val });
                                            if (fulfillmentErrors.delivery_fee) {
                                                setFulfillmentErrors(prev => ({ ...prev, delivery_fee: undefined }));
                                            }
                                        }}
                                        onKeyDown={(e) => {
                                            if (["-", "+", "e", "E"].includes(e.key)) {
                                                e.preventDefault();
                                            }
                                        }}
                                        style={{ borderRadius: "6px" }}
                                    />
                                    {fulfillmentErrors.delivery_fee && (
                                        <span className="text-danger" style={{ fontSize: "12px", display: "block", marginTop: "4px", fontWeight: 500 }}>
                                            {fulfillmentErrors.delivery_fee}
                                        </span>
                                    )}
                                </div>

                                <div className="col-md-4 mb-3">
                                    <label className="form-label" style={{ fontWeight: 600, fontSize: "13px" }}>
                                        Packing Charge (₹)
                                    </label>
                                    <Input
                                        type="text"
                                        min="0"
                                        step="any"
                                        maxLength="7"
                                        size="large"
                                        placeholder="0.00"
                                        value={fulfillmentForm.packing_charge}
                                        status={fulfillmentErrors.packing_charge ? "error" : ""}
                                        onChange={(e) => {
                                            let val = e.target.value;
                                            val = val.replace(/[^0-9.]/g, "");
                                            const parts = val.split(".");
                                            if (parts.length > 2) val = parts[0] + "." + parts.slice(1).join("");
                                            if (parts[0] && parts[0].length > 4) parts[0] = parts[0].slice(0, 4);
                                            if (parts[1] && parts[1].length > 2) parts[1] = parts[1].slice(0, 2);
                                            val = parts.length > 1 ? parts[0] + "." + parts[1] : parts[0];
                                            setFulfillmentForm({ ...fulfillmentForm, packing_charge: val });
                                            if (fulfillmentErrors.packing_charge) {
                                                setFulfillmentErrors(prev => ({ ...prev, packing_charge: undefined }));
                                            }
                                        }}
                                        onKeyDown={(e) => {
                                            if (["-", "+", "e", "E"].includes(e.key)) {
                                                e.preventDefault();
                                            }
                                        }}
                                        style={{ borderRadius: "6px" }}
                                    />
                                    {fulfillmentErrors.packing_charge && (
                                        <span className="text-danger" style={{ fontSize: "12px", display: "block", marginTop: "4px", fontWeight: 500 }}>
                                            {fulfillmentErrors.packing_charge}
                                        </span>
                                    )}
                                </div>

                                <div className="col-md-4 mb-3">
                                    <label className="form-label" style={{ fontWeight: 600, fontSize: "13px" }}>
                                        Miscellaneous Charge (₹)
                                    </label>
                                    <Input
                                        type="text"
                                        min="0"
                                        step="any"
                                        maxLength="7"
                                        size="large"
                                        placeholder="0.00"
                                        value={fulfillmentForm.miscellaneous_charge}
                                        status={fulfillmentErrors.miscellaneous_charge ? "error" : ""}
                                        onChange={(e) => {
                                            let val = e.target.value;
                                            val = val.replace(/[^0-9.]/g, "");
                                            const parts = val.split(".");
                                            if (parts.length > 2) val = parts[0] + "." + parts.slice(1).join("");
                                            if (parts[0] && parts[0].length > 4) parts[0] = parts[0].slice(0, 4);
                                            if (parts[1] && parts[1].length > 2) parts[1] = parts[1].slice(0, 2);
                                            val = parts.length > 1 ? parts[0] + "." + parts[1] : parts[0];
                                            setFulfillmentForm({ ...fulfillmentForm, miscellaneous_charge: val });
                                            if (fulfillmentErrors.miscellaneous_charge) {
                                                setFulfillmentErrors(prev => ({ ...prev, miscellaneous_charge: undefined }));
                                            }
                                        }}
                                        onKeyDown={(e) => {
                                            if (["-", "+", "e", "E"].includes(e.key)) {
                                                e.preventDefault();
                                            }
                                        }}
                                        style={{ borderRadius: "6px" }}
                                    />
                                    {fulfillmentErrors.miscellaneous_charge && (
                                        <span className="text-danger" style={{ fontSize: "12px", display: "block", marginTop: "4px", fontWeight: 500 }}>
                                            {fulfillmentErrors.miscellaneous_charge}
                                        </span>
                                    )}
                                </div>
                            </div>

                            <div className="d-flex justify-content-end  mt-3" style={{ gap: "8px" }}>
                                <Button
                                    onClick={() => {
                                        setShowFulfillmentForm(false);
                                        setFulfillmentEditId(null);
                                    }}
                                    style={{ borderRadius: "6px" }}
                                >
                                    Cancel
                                </Button>
                                <Button
                                    type="primary"
                                    onClick={saveFulfillment}
                                    loading={isFulfillmentLoading}
                                    style={{ borderRadius: "6px", fontWeight: 600, minWidth: "140px" }}
                                >
                                    {fulfillmentEditId ? "Update Fulfillment" : "Create Fulfillment"}
                                </Button>
                            </div>
                        </div>
                    )}
                </div>
            ),
        },
        {
            key: '4',
            label: (
                <span className="d-flex align-items-center" style={{ gap: "8px", fontWeight: 600, fontSize: "14px" }}>
                    <FaMapMarkedAlt size={16} />
                    Serviceability
                    {serviceabilityList.length > 0 && <Tag color="geekblue" style={{ marginLeft: 4, borderRadius: 10, padding: "0 6px", fontSize: 11 }}>{serviceabilityList.length}</Tag>}
                </span>
            ),
            children: (
                <div style={{ backgroundColor: "#FFFFFF", padding: "24px", borderRadius: "10px", border: "1px solid #E2E8F0" }}>
                    <div className="d-flex justify-content-between align-items-center mb-4 pb-2 flex-wrap" style={{ borderBottom: "1px solid #F1F5F9", gap: "12px" }}>
                        <div>
                            <h5 style={{ fontWeight: 700, margin: 0, color: "#0F172A" }}>
                                Warehouse Serviceability
                            </h5>
                            <p style={{ fontSize: "13px", color: "#64748B", margin: 0 }}>
                                Define hyperlocal ranges, serviced cities, or pan-India delivery zones for this warehouse.
                            </p>
                        </div>
                        <div className="d-flex align-items-center" style={{ gap: "8px" }}>
                            {!showServiceabilityForm ? (
                                !hasServiceability && (
                                    <Button
                                        type="primary"
                                        icon={<FaPlus size={12} style={{ marginRight: 6 }} />}
                                        onClick={() => {
                                            // Reset form to blank for fresh Create (not Edit)
                                            setUserChangedHyperCity(false);
                                            setServiceFeature("");
                                            setServiceData({ service_type: "", service_range: "", service_city: "", served_city_list: [] });
                                            setSelectedServiceCities([]);
                                            setServiceabilityEditId(null);
                                            setShowServiceabilityForm(true);
                                        }}
                                        style={{ borderRadius: "6px", fontWeight: 600 }}
                                    >
                                        Create Serviceability
                                    </Button>
                                )
                            ) : (
                                <Button
                                    onClick={() => setShowServiceabilityForm(false)}
                                    style={{ borderRadius: "6px", fontWeight: 500 }}
                                >
                                    ← View All Serviceability
                                </Button>
                            )}

                        </div>
                    </div>

                    {!effectiveWarehouseId && (
                        <div
                            className="alert alert-info d-flex align-items-center mb-3 py-2 px-3"
                            style={{ borderRadius: "8px", fontSize: "13px", backgroundColor: "#EFF6FF", border: "1px solid #BFDBFE", color: "#1E40AF" }}
                        >
                            <FaInfoCircle size={16} className="mr-2" />
                            <span>
                                You are creating a new warehouse. Save <strong>Warehouse Details (Tab 1)</strong> first to enable adding serviceability zones.
                            </span>
                        </div>
                    )}

                    {!showServiceabilityForm ? (
                        <div>
                            <Table
                                columns={serviceabilityColumns}
                                dataSource={serviceabilityList}
                                rowKey="id"
                                loading={isServiceabilityLoading}
                                pagination={false}
                                scroll={{ x: true }}
                            />
                        </div>
                    ) : (
                        <div className="p-3" style={{ backgroundColor: "#F8FAFC", borderRadius: "10px", border: "1px solid #E2E8F0" }}>
                            <h6 style={{ fontWeight: 700, color: "#0F172A", marginBottom: "16px" }}>
                                {serviceabilityEditId ? "Update Serviceability" : "Create Serviceability"}
                            </h6>
                            <div className="row">

                                <div className="col-md-12 mb-3">
                                    <label className="form-label" style={{ fontWeight: 600, fontSize: "13px" }}>
                                        Serviceability Type <span className="text-danger">*</span>
                                    </label>
                                    <select
                                        name="service_type"
                                        className="form-control"
                                        value={serviceData.service_type}
                                        onChange={(e) => setServiceData({ ...serviceData, service_type: e.target.value })}
                                        style={{ height: "40px", borderRadius: "6px" }}
                                    >
                                        <option value="">Select Serviceability Type</option>
                                        <option value="hyperlocal">Hyper Local</option>
                                        <option value="intercity">Intercity</option>
                                        <option value="pan-india">Pan India</option>
                                    </select>
                                </div>

                                {serviceData.service_type === "hyperlocal" && (
                                    <div className="col-md-12 mb-3 p-3 bg-white" style={{ borderRadius: "8px", border: "1px solid #E2E8F0" }}>
                                        <div className="row">
                                            <div className="col-md-6 mb-2">
                                                <label className="form-label" style={{ fontWeight: 600, fontSize: "13px" }}>
                                                    Area Range (in Km) <span className="text-danger">*</span>
                                                </label>
                                                <Input
                                                    type="text"
                                                    maxLength={3}
                                                    placeholder="e.g. 100"
                                                    value={serviceData.service_range}
                                                    status={Number(serviceData.service_range) > 100 ? "error" : undefined}
                                                    onChange={(e) => {
                                                        const val = e.target.value.replace(/\D/g, "").slice(0, 3);
                                                        setServiceData({ ...serviceData, service_range: val });
                                                    }}
                                                    onKeyDown={(e) => {
                                                        if (["-", "+", "e", "E", "."].includes(e.key)) {
                                                            e.preventDefault();
                                                        }
                                                    }}
                                                    style={{ borderRadius: "6px" }}
                                                />
                                                {Number(serviceData.service_range) > 100 && (
                                                    <span className="text-danger mt-1 d-block" style={{ fontSize: "12px", fontWeight: 500 }}>
                                                        Area range cannot be greater than 100 Km!
                                                    </span>
                                                )}
                                            </div>
                                            <div className="col-md-6 mb-2">
                                                <label className="form-label" style={{ fontWeight: 600, fontSize: "13px" }}>Serviced City</label>

                                                {(() => {
                                                    const existingCities = Array.isArray(serviceData.served_city_list) ? serviceData.served_city_list : [];
                                                    const PREVIEW = 2;
                                                    const previewCities = existingCities.slice(0, PREVIEW);
                                                    const remaining = existingCities.length - PREVIEW;
                                                    const placeholderText = !userChangedHyperCity && existingCities.length > 0
                                                        ? previewCities.join(", ") + (remaining > 0 ? ` ... +${remaining} more (${existingCities.length} total)` : ` (${existingCities.length} total)`)
                                                        : (isCitiesLoading ? "Loading cities..." : "Select a city to update");
                                                    return (
                                                        <Select
                                                            allowClear
                                                            showSearch
                                                            style={{ width: "100%" }}
                                                            placeholder={placeholderText}
                                                            value={userChangedHyperCity ? (serviceData.service_city || undefined) : undefined}
                                                            onChange={(value) => {
                                                                setUserChangedHyperCity(true);
                                                                setServiceData({
                                                                    ...serviceData,
                                                                    service_city: value || "",
                                                                    served_city_list: value
                                                                        ? [value]
                                                                        : (existingCities.length > 0 ? existingCities : [])
                                                                });
                                                            }}
                                                            onClear={() => {
                                                                // When cleared, keep existing cities so backend doesn't get empty list
                                                                setUserChangedHyperCity(false);
                                                                setServiceData({
                                                                    ...serviceData,
                                                                    service_city: existingCities[0] || "",
                                                                    served_city_list: existingCities
                                                                });
                                                            }}
                                                            options={cityOptions}
                                                            optionFilterProp="label"
                                                            filterOption={(input, option) =>
                                                                (option?.label ?? '').toLowerCase().includes(input.toLowerCase())
                                                            }
                                                            loading={isCitiesLoading}
                                                        />
                                                    );
                                                })()}
                                            </div>
                                        </div>
                                    </div>
                                )}

                                {serviceData.service_type === "intercity" && (
                                    <div className="col-md-12 mb-3 p-3 bg-white" style={{ borderRadius: "8px", border: "1px solid #E2E8F0" }}>
                                        <label className="form-label" style={{ fontWeight: 600, fontSize: "13px" }}>Serviced Cities/State/Zone</label>
                                        <Select
                                            mode="multiple"
                                            allowClear
                                            style={{ width: "100%" }}
                                            placeholder="Search and select cities"
                                            value={selectedServiceCities}
                                            onChange={(value) => setSelectedServiceCities(value)}
                                            options={cityOptions}
                                            showSearch
                                            optionFilterProp="label"
                                            filterOption={(input, option) =>
                                                (option?.label ?? '').toLowerCase().includes(input.toLowerCase())
                                            }
                                            loading={isCitiesLoading}
                                        />
                                    </div>
                                )}
                            </div>

                            <div className="d-flex justify-content-end gap-2 mt-3">
                                <Button
                                    onClick={() => setShowServiceabilityForm(false)}
                                    style={{ borderRadius: "6px" }}
                                >
                                    Cancel
                                </Button>
                                <Button
                                    type="primary"
                                    onClick={saveServiceability}
                                    loading={isServiceabilityLoading}
                                    disabled={
                                        serviceData.service_type === "hyperlocal" &&
                                        (!serviceData.service_range || Number(serviceData.service_range) > 100 || Number(serviceData.service_range) <= 0)
                                    }
                                    style={{ borderRadius: "6px", fontWeight: 600, minWidth: "160px", marginLeft: "5px" }}
                                >
                                    {serviceabilityEditId ? "Update Serviceability" : "Create Serviceability"}
                                </Button>
                            </div>
                        </div>
                    )}
                </div>
            ),
        },
        {
            key: '5',
            label: (
                <span className="d-flex align-items-center" style={{ gap: "8px", fontWeight: 600, fontSize: "14px" }}>
                    <FaMoneyCheckAlt size={16} />
                    Bank Details
                    {bankDetails && (bankDetails?.accountnumber || bankDetails?.account_number) ? (
                        <FaCheckCircle color="#16A34A" size={14} />
                    ) : null}
                </span>
            ),
            children: (
                <div style={{ backgroundColor: "#FFFFFF", padding: "24px", borderRadius: "10px", border: "1px solid #E2E8F0" }}>
                    <div className="d-flex justify-content-between align-items-center mb-4 pb-3 flex-wrap" style={{ borderBottom: "1px solid #F1F5F9", gap: "12px" }}>
                        <div>
                            <h5 style={{ fontWeight: 700, margin: 0, color: "#0F172A", display: "flex", alignItems: "center", gap: "8px" }}>
                                Bank Account Information
                                {isSellerEditable ? (
                                    <Tag color="orange" style={{ fontWeight: 600, fontSize: "11px" }}>Editable</Tag>
                                ) : (
                                    <Tag color="default" style={{ fontSize: "11px" }}>Read Only</Tag>
                                )}
                            </h5>
                            <p style={{ fontSize: "13px", color: "#64748B", margin: "4px 0 0 0" }}>
                                {isSellerEditable
                                    ? "Verified bank account details linked to your warehouse. You can edit and update details."
                                    : "Verified bank account details linked to this seller account (View-Only)."}
                            </p>
                        </div>
                        <div className="d-flex align-items-center" style={{ gap: "10px" }}>
                            {isSellerEditable ? (
                                <Button
                                    type="primary"
                                    icon={<FaEdit style={{ marginRight: 6 }} />}
                                    onClick={openBankEditModal}
                                    style={{
                                        borderRadius: "6px",
                                        fontWeight: 600,
                                        background: "linear-gradient(90deg, #F75710, #FC8809)",
                                        border: "none",
                                        boxShadow: "0 2px 6px rgba(247, 87, 16, 0.25)"
                                    }}
                                >
                                    Edit Bank Details
                                </Button>
                            ) : null}

                        </div>
                    </div>

                    {isBankLoading ? (
                        <div className="text-center py-5 text-muted">
                            <div className="spinner-border spinner-border-sm text-primary mb-2" role="status"></div>
                            <p style={{ fontSize: "14px" }}>Loading bank details...</p>
                        </div>
                    ) : bankDetails && (bankDetails?.accountnumber || bankDetails?.account_number || bankDetails?.name) ? (
                        <div>
                            <div
                                style={{
                                    display: "grid",
                                    gridTemplateColumns: "repeat(auto-fill, minmax(280px, 1fr))",
                                    gap: "16px",
                                    marginBottom: "24px"
                                }}
                            >
                                <div style={{ backgroundColor: "#F8FAFC", border: "1px solid #E2E8F0", borderRadius: "10px", padding: "16px" }}>
                                    <span style={{ fontSize: "12px", color: "#64748B", fontWeight: 600, textTransform: "uppercase" }}>
                                        Account Holder Name
                                    </span>
                                    <h6 style={{ fontWeight: 700, color: "#0F172A", marginTop: "6px", marginBottom: 0 }}>
                                        {bankDetails?.name || bankDetails?.account_holder_name || "—"}
                                    </h6>
                                </div>

                                <div style={{ backgroundColor: "#F8FAFC", border: "1px solid #E2E8F0", borderRadius: "10px", padding: "16px" }}>
                                    <span style={{ fontSize: "12px", color: "#64748B", fontWeight: 600, textTransform: "uppercase" }}>
                                        Account Number
                                    </span>
                                    <h6 style={{ fontWeight: 700, color: "#0F172A", marginTop: "6px", marginBottom: 0, letterSpacing: "1px" }}>
                                        {bankDetails?.accountnumber || bankDetails?.account_number || "—"}
                                    </h6>
                                </div>

                                <div style={{ backgroundColor: "#F8FAFC", border: "1px solid #E2E8F0", borderRadius: "10px", padding: "16px" }}>
                                    <span style={{ fontSize: "12px", color: "#64748B", fontWeight: 600, textTransform: "uppercase" }}>
                                        IFSC Code
                                    </span>
                                    <h6 style={{ fontWeight: 700, color: "#0F172A", marginTop: "6px", marginBottom: 0 }}>
                                        {bankDetails?.ifsc || bankDetails?.ifsc_code || "—"}
                                    </h6>
                                </div>

                                <div style={{ backgroundColor: "#F8FAFC", border: "1px solid #E2E8F0", borderRadius: "10px", padding: "16px" }}>
                                    <span style={{ fontSize: "12px", color: "#64748B", fontWeight: 600, textTransform: "uppercase" }}>
                                        Bank Name
                                    </span>
                                    <h6 style={{ fontWeight: 700, color: "#0F172A", marginTop: "6px", marginBottom: 0 }}>
                                        {bankDetails?.bank_name || bankDetails?.bankname || "—"}
                                    </h6>
                                </div>

                                <div style={{ backgroundColor: "#F8FAFC", border: "1px solid #E2E8F0", borderRadius: "10px", padding: "16px" }}>
                                    <span style={{ fontSize: "12px", color: "#64748B", fontWeight: 600, textTransform: "uppercase" }}>
                                        Branch Name
                                    </span>
                                    <h6 style={{ fontWeight: 700, color: "#0F172A", marginTop: "6px", marginBottom: 0 }}>
                                        {bankDetails?.branch_name || bankDetails?.branchname || "—"}
                                    </h6>
                                </div>

                                <div style={{ backgroundColor: "#F8FAFC", border: "1px solid #E2E8F0", borderRadius: "10px", padding: "16px" }}>
                                    <span style={{ fontSize: "12px", color: "#64748B", fontWeight: 600, textTransform: "uppercase" }}>
                                        Verification Status
                                    </span>
                                    <div style={{ marginTop: "6px" }}>
                                        <Tag color={bankDetails?.is_verified || bankDetails?.status ? "green" : "blue"} style={{ fontWeight: 600, fontSize: "12px" }}>
                                            {bankDetails?.is_verified || bankDetails?.status ? "Verified Account" : "Linked Account"}
                                        </Tag>
                                    </div>
                                </div>
                            </div>

                            {(bankDetails?.cancelled_cheque_url || bankDetails?.cheque_url) && (
                                <div className="mt-3 p-3" style={{ border: "1px dashed #CBD5E1", borderRadius: "8px", backgroundColor: "#F8FAFC" }}>
                                    <strong style={{ fontSize: "13px", color: "#334155" }}>Cancelled Cheque Document:</strong>
                                    <div className="mt-2">
                                        <a
                                            href={bankDetails?.cancelled_cheque_url || bankDetails?.cheque_url}
                                            target="_blank"
                                            rel="noopener noreferrer"
                                            className="btn btn-sm btn-outline-primary"
                                            style={{ borderRadius: "6px" }}
                                        >
                                            📄 View Uploaded Cheque Document
                                        </a>
                                    </div>
                                </div>
                            )}
                        </div>
                    ) : (
                        <div className="text-center py-5" style={{ backgroundColor: "#F8FAFC", borderRadius: "10px", border: "1px dashed #CBD5E1" }}>
                            <FaMoneyCheckAlt size={40} color="#94A3B8" className="mb-2" />
                            <h6 style={{ fontWeight: 600, color: "#475569" }}>No Bank Details Found</h6>
                            <p style={{ fontSize: "13px", color: "#64748B", maxWidth: "420px", margin: "0 auto" }}>
                                Bank details have not been linked yet for this seller account.
                            </p>
                        </div>
                    )}
                </div>
            ),
        }
    ];

    return (
        <ThemeMaker>
            <div className="row m-0 w-100 mt-2">
                <div className="col-md-12" style={{ height: "100vh", overflow: "scroll" }}>
                    <div className="card shadow-sm border-0" style={{ borderRadius: "12px", backgroundColor: "#FFFFFF" }}>
                        <div className="card-body p-4" style={{ minHeight: "82vh" }}>
                            <div className="d-flex justify-content-between align-items-center mb-3 flex-wrap" style={{ gap: "12px" }}>
                                <div>
                                    <h4 style={{ fontWeight: 700, margin: 0, color: "#0F172A" }}>
                                        {effectiveWarehouseId ? "Warehouse Management (Edit)" : "Warehouse Setup (Add New)"}
                                    </h4>
                                    <p style={{ fontSize: "13px", color: "#64748B", margin: 0 }}>
                                        {effectiveWarehouseId
                                            ? `Manage details, operating hours, fulfillments, and serviceability for Warehouse #${effectiveWarehouseId}`
                                            : "Create and configure a new warehouse for your store"}
                                    </p>
                                </div>
                                <div className="d-flex align-items-center" style={{ gap: "10px" }}>
                                    <button
                                        type="button"
                                        className="btn btn-outline-secondary btn-sm"
                                        style={{ borderRadius: "6px", fontWeight: 500 }}
                                        onClick={() => router.push('/sellerdetail')}
                                    >
                                        ← Back to Warehouses
                                    </button>
                                </div>
                            </div>

                            <Tabs
                                activeKey={activeTab}
                                onChange={(k) => {
                                    setActiveTab(k);
                                    const whKey = routeWarehouseId || effectiveWarehouseId || selectedTimingWarehouseId || "default";
                                    sessionStorage.setItem(`active_wh_tab_${whKey}`, k);
                                    sessionStorage.setItem("active_warehouse_tab", k);

                                    const targetWhId = effectiveWarehouseId || routeWarehouseId || selectedTimingWarehouseId;
                                    const targetLocId = effectiveLocationId || routeLocationId || targetWhId;
                                    if (k === "4") {
                                        if (targetWhId) {
                                            router.push(`/serviceability?id=${targetWhId}&warehouse_location_id=${targetLocId || targetWhId}&tab=4`);
                                        } else {
                                            toast.info("Please save Warehouse Details (Tab 1) first to configure Serviceability.");
                                        }
                                    } else if (k === "3") {
                                        if (targetWhId) {
                                            router.push(`/warehousefulfillment?id=${targetWhId}&warehouse_location_id=${targetLocId || targetWhId}&tab=3`);
                                        } else {
                                            router.push(`/warehousefulfillment?tab=3`);
                                        }
                                    } else {
                                        const currentParams = new URLSearchParams(location.search);
                                        currentParams.set("tab", k);
                                        if (targetWhId) {
                                            currentParams.set("id", String(targetWhId));
                                        }
                                        router.replace({
                                            pathname: location.pathname,
                                            search: currentParams.toString()
                                        });
                                    }
                                }}
                                items={tabItems}
                                type="card"
                                style={{ marginTop: "16px" }}
                            />
                        </div>
                    </div>
                </div>
            </div>

            {/* Edit Bank Details Modal (Only for parent_id === 1) */}
            <Modal
                title={
                    <div className="d-flex align-items-center" style={{ gap: "8px" }}>
                        <FaMoneyCheckAlt color="#F75710" size={18} />
                        <span style={{ fontWeight: 700, fontSize: "16px", color: "#0F172A" }}>Update Warehouse Bank Details</span>
                    </div>
                }
                open={isBankEditModalOpen}
                onCancel={() => {
                    setIsBankEditModalOpen(false);
                    setIsBankVerifiedInModal(false);
                    setVerifiedBankData(null);
                }}
                footer={null}
                centered
                destroyOnClose
                width={580}
            >
                <Form
                    form={bankEditForm}
                    layout="vertical"
                    style={{ marginTop: "12px" }}
                >
                    <Form.Item
                        name="account_holder_name"
                        label={<span style={{ fontWeight: 600, fontSize: "13px" }}>Account Holder Name</span>}
                        rules={[
                            { required: true, message: "Please enter account holder name!" },
                            {
                                validator: (_, value) => {
                                    if (!value) return Promise.resolve();
                                    if (/[@#$%^&*!~+=<>?\\{}()[\]|`"';:_0-9]/.test(value)) {
                                        return Promise.reject(new Error("Special characters (@, #, $, %, etc.) and numbers are not allowed!"));
                                    }
                                    if (value.trim().length < 3) {
                                        return Promise.reject(new Error("Name must be at least 3 characters long!"));
                                    }
                                    if (!/^[a-zA-Z\s/.\-]+$/.test(value.trim())) {
                                        return Promise.reject(new Error("Only alphabets, spaces, and valid prefixes (e.g. S/O, D/O) are allowed!"));
                                    }
                                    return Promise.resolve();
                                }
                            }
                        ]}
                        normalize={(value) =>
                            (value || "")
                                .toUpperCase()
                                .replace(/[^A-Z\s/.\-]/g, "")
                                .replace(/^\s+/, "")
                                .replace(/\s{2,}/g, " ")
                                .slice(0, 70)
                        }
                    >
                        <Input
                            placeholder="Enter Name"
                            maxLength={70}
                            style={{ borderRadius: "6px", height: "40px", textTransform: "uppercase" }}
                        />
                    </Form.Item>

                    <div className="row m-0 p-0" style={{ gap: "12px", display: "flex" }}>
                        <div style={{ flex: 1 }}>
                            <Form.Item
                                name="account_no"
                                label={<span style={{ fontWeight: 600, fontSize: "13px" }}>Account Number</span>}
                                rules={[
                                    { required: true, message: "Please enter account number!" },
                                    {
                                        validator: (_, value) => {
                                            if (!value) return Promise.resolve();
                                            if (/\s/.test(value)) {
                                                return Promise.reject(new Error("Spaces are not allowed in account number!"));
                                            }
                                            if (!/^[0-9]{9,18}$/.test(value.toString().trim())) {
                                                return Promise.reject(new Error("Account number must be between 9 and 18 digits!"));
                                            }
                                            return Promise.resolve();
                                        }
                                    }
                                ]}
                                normalize={(value) => (value || "").toString().replace(/\D/g, "").slice(0, 18)}
                            >
                                <Input
                                    placeholder="e.g. 40900725947"
                                    maxLength={18}
                                    style={{ borderRadius: "6px", height: "40px", letterSpacing: "1px" }}
                                />
                            </Form.Item>
                        </div>

                        <div style={{ width: "160px" }}>
                            <Form.Item
                                name="account_type"
                                label={<span style={{ fontWeight: 600, fontSize: "13px" }}>Account Type</span>}
                                rules={[{ required: true, message: "Please select account type!" }]}
                                initialValue="savings"
                            >
                                <Select
                                    style={{ height: "40px" }}
                                    options={[
                                        { label: "Savings Account", value: "savings" },
                                        { label: "Current Account", value: "current" }
                                    ]}
                                />
                            </Form.Item>
                        </div>
                    </div>

                    <Form.Item
                        name="ifsc_code"
                        label={<span style={{ fontWeight: 600, fontSize: "13px" }}>IFSC Code</span>}
                        extra={
                            modalIfscLoading ? (
                                <span style={{ fontSize: "12px", color: "#64748B", marginTop: "4px", display: "inline-flex", alignItems: "center", gap: "6px" }}>
                                    <Spin size="small" /> Fetching bank details...
                                </span>
                            ) : modalIfscInfo?.bank || modalIfscInfo?.branch ? (
                                <span style={{ fontSize: "12px", color: "#16A34A", marginTop: "4px", display: "block", fontWeight: 600 }}>
                                    🏛️ {modalIfscInfo.bank} {modalIfscInfo.branch ? `• ${modalIfscInfo.branch}` : ""}
                                </span>
                            ) : null
                        }
                        rules={[
                            { required: true, message: "Please enter IFSC code!" },
                            {
                                validator: (_, value) => {
                                    if (!value) return Promise.resolve();
                                    const upper = value.trim().toUpperCase();
                                    if (/[@#$%^&*!~+=<>?/\\{}()[\]|`"';:,._\s-]/.test(value)) {
                                        return Promise.reject(new Error("Special characters (@, #, etc.) and spaces are not allowed!"));
                                    }
                                    if (upper.length < 11) {
                                        return Promise.reject(new Error("IFSC code must be exactly 11 characters long!"));
                                    }
                                    if (upper.length >= 5 && upper[4] !== "0") {
                                        return Promise.reject(new Error("5th character of IFSC must be '0' (zero)!"));
                                    }
                                    if (!/^[A-Z]{4}0[A-Z0-9]{6}$/.test(upper)) {
                                        return Promise.reject(new Error("Invalid IFSC format! Example: SBIN0018101"));
                                    }
                                    return Promise.resolve();
                                }
                            }
                        ]}
                        normalize={(value) => (value || "").toUpperCase().replace(/[^A-Z0-9]/g, "").slice(0, 11)}
                    >
                        <Input
                            placeholder="e.g. SBIN0018101"
                            maxLength={11}
                            style={{ borderRadius: "6px", height: "40px", textTransform: "uppercase" }}
                            onChange={(e) => {
                                const val = (e.target.value || "").toUpperCase().replace(/[^A-Z0-9]/g, "").slice(0, 11);
                                if (val.length === 11 && /^[A-Z]{4}0[A-Z0-9]{6}$/.test(val)) {
                                    fetchIfscLookup(val);
                                } else {
                                    setModalIfscInfo({ bank: "", branch: "" });
                                }
                            }}
                        />
                    </Form.Item>

                    <div className="d-flex justify-content-between align-items-center mt-4 pt-3 flex-wrap" style={{ borderTop: "1px solid #F1F5F9", gap: "10px" }}>
                        <Button
                            onClick={() => {
                                setIsBankEditModalOpen(false);
                                setIsBankVerifiedInModal(false);
                                setVerifiedBankData(null);
                            }}
                            style={{ borderRadius: "6px" }}
                        >
                            Cancel
                        </Button>

                        <Button
                            type="primary"
                            onClick={handleUpdateBankDetails}
                            loading={isBankUpdating}
                            style={{
                                borderRadius: "6px",
                                background: "linear-gradient(90deg, #F75710, #FC8809)",
                                borderColor: "#F75710",
                                color: "#FFFFFF",
                                fontWeight: 600,
                                boxShadow: "0 2px 6px rgba(247, 87, 16, 0.25)"
                            }}
                        >
                            {isBankUpdating ? "Updating..." : "Update Bank Details"}
                        </Button>
                    </div>
                </Form>
            </Modal>
        </ThemeMaker>
    );
}